import { useState, useEffect, useRef, useCallback } from 'react';
import { money } from '../events/util';
import QRCode from 'qrcode';
import { reportTpvError, reportTpvHeartbeat, HEARTBEAT_MS } from '../lib/telemetry';
import ErrorBoundary from '../components/ErrorBoundary';
import HangerGrid from '../tpv/HangerGrid';
import { connectBluetoothPrinter, printBluetooth, printBrowser, ticketLines, bluetoothAvailable,
  nativePrinterAvailable, listNativePrinters, connectNativePrinter, printNativeSPP, printBoleteriaTickets, printVales } from '../tpv/printer';

const API = process.env.REACT_APP_API_URL || '';

// Topes de tiempo del TPV. El cobro es lo mas critico: si la red no responde,
// preferimos cortar y encolar (la venta NO se pierde y no se duplica, porque va con
// client_sale_id) antes que dejar la caja colgada con gente esperando.
const SALE_TIMEOUT_MS = 4000;
const PRINT_TIMEOUT_MS = 12000;

// Le pone un tope a cualquier promesa que pueda no terminar nunca (la impresora
// Bluetooth es el caso típico: si está apagada o fuera de rango, el await se queda
// esperando y la caja no vuelve a manos del operador). El trabajo original sigue su
// curso; lo que se corta es la ESPERA.
function conTimeout(promesa, ms) {
  return new Promise((resolve, reject) => {
    const t = setTimeout(() => reject(new Error('TIMEOUT')), ms);
    promesa.then(
      (v) => { clearTimeout(t); resolve(v); },
      (e) => { clearTimeout(t); reject(e); },
    );
  });
}
const STORE_KEY = 'tk_tpv_session';
const LINK_KEY = 'tk_tpv_link'; // boliche vinculado: { token, slug, name }

/* ---------------- APERTURA SIN INTERNET ----------------
   El TPV vende sin señal, pero ABRIR la caja pegaba siempre al servidor: si un cajero
   cerraba sesión en medio de la noche y no había internet, la tablet quedaba afuera con
   la caja cargada. Ahora, cada vez que una caja se abre CON internet, el aparato guarda:
     · la lista de eventos y cajas que vio (para poder elegir lo mismo sin señal),
     · un verificador del PIN —PBKDF2, no el PIN— para validarlo localmente,
     · la sesión y un token de refresco para subir las ventas cuando vuelva la señal.
   Solo se pueden abrir sin internet las cajas ya abiertas en ESE aparato: así no sale
   del servidor ningún PIN que la tablet no tuviera ya. */
const CRED_PREFIX = 'tk_tpv_cred_';            // credencial offline, una por caja
const CACHE_EVENTS_KEY = 'tk_tpv_cache_events';
const cacheBoxesKey = (eventId) => `tk_tpv_cache_boxes_${eventId}`;
const PBKDF2_ITERS = 150000;
const MAX_INTENTOS = 5;                        // antes de frenar un minuto
const BLOQUEO_MS = 60000;

const leerJSON = (k, fallback = null) => { try { return JSON.parse(localStorage.getItem(k) || 'null') ?? fallback; } catch { return fallback; } };
const guardarJSON = (k, v) => { try { localStorage.setItem(k, JSON.stringify(v)); } catch { /* sin espacio: no es fatal */ } };
const aB64 = (buf) => { const b = new Uint8Array(buf); let s = ''; for (const n of b) s += String.fromCharCode(n); return btoa(s); };

// PBKDF2-SHA256 del PIN. Un PIN de 4 dígitos es adivinable a fuerza bruta por quien
// tenga la tablet en la mano; las iteraciones encarecen el intento y, sobre todo, el
// PIN no queda escrito en ningún lado. Necesita contexto seguro (https o la app).
async function hashPin(pin, saltB64, iters) {
  const sub = typeof crypto !== 'undefined' && crypto.subtle;
  if (!sub) return null;
  const salt = Uint8Array.from(atob(saltB64), c => c.charCodeAt(0));
  const key = await sub.importKey('raw', new TextEncoder().encode(String(pin)), 'PBKDF2', false, ['deriveBits']);
  const bits = await sub.deriveBits({ name: 'PBKDF2', salt, iterations: iters, hash: 'SHA-256' }, key, 256);
  return aB64(bits);
}

async function guardarCredencial({ eventId, boxId, pin, token, refresh, session }) {
  const sub = typeof crypto !== 'undefined' && crypto.subtle;
  if (!sub || !refresh) return;   // servidor viejo o contexto inseguro: sin apertura offline
  const salt = aB64(crypto.getRandomValues(new Uint8Array(16)));
  const hash = await hashPin(pin, salt, PBKDF2_ITERS);
  if (!hash) return;
  guardarJSON(CRED_PREFIX + boxId, { v: 1, eventId, boxId, salt, hash, iters: PBKDF2_ITERS, token, refresh, session, savedAt: Date.now(), fails: 0, lockUntil: 0 });
}
const leerCredencial = (boxId) => leerJSON(CRED_PREFIX + boxId);

/* ---------------- VENTAS QUE QUEDARON SIN SUBIR ----------------
   Las ventas sin red se guardan en la tablet y suben solas... pero SOLO mientras la
   caja está abierta. Pasó el 2026-10-18: tres tablets vendieron sin señal toda la
   noche, los cajeros cerraron sesión al terminar, y al día siguiente el evento ya no
   estaba en el selector — la plata cobrada quedó encerrada en el aparato y el panel
   mostraba las cajas en cero. Ahora la pantalla de abrir caja avisa y las sube con el
   PIN de esa caja, sin depender de la lista de eventos ni de que el evento siga vivo. */
const QUEUE_PREFIX = 'tk_tpv_queue_';
const RET_PREFIX = 'tk_tpv_ret_';
const BOXINFO_PREFIX = 'tk_tpv_boxinfo_';   // nombre de caja/evento, para poder nombrarlas

// Deja escrito a qué caja y evento pertenece un boxId. Las tablets viejas no lo tienen:
// ahí la fila se muestra igual, con lo que se pueda (cantidad y monto).
const guardarBoxInfo = (s) => guardarJSON(BOXINFO_PREFIX + s.boxId, { boxName: s.boxName, eventName: s.eventName, at: Date.now() });

function colasPendientes() {
  const ids = new Set();
  try {
    for (const k of Object.keys(localStorage)) {
      if (k.startsWith(QUEUE_PREFIX)) ids.add(k.slice(QUEUE_PREFIX.length));
      else if (k.startsWith(RET_PREFIX)) ids.add(k.slice(RET_PREFIX.length));
    }
  } catch { return []; }
  const out = [];
  for (const boxId of ids) {
    const ventas = leerJSON(QUEUE_PREFIX + boxId, []) || [];
    const devoluciones = leerJSON(RET_PREFIX + boxId, []) || [];
    if (!ventas.length && !devoluciones.length) continue;
    const info = leerJSON(BOXINFO_PREFIX + boxId) || {};
    out.push({
      boxId, ventas: ventas.length, devoluciones: devoluciones.length,
      total: ventas.reduce((a, s) => a + Number(s.total || 0), 0),
      boxName: info.boxName || '', eventName: info.eventName || '',
    });
  }
  return out;
}

// Permiso para subir, por las dos vías posibles:
//  · el que quedó guardado al abrir la caja (no molesta a nadie: sube solo), o
//  · el PIN de la caja, para las tablets que todavía no tienen nada guardado.
async function tokenPorRefresh(boxId) {
  const cred = leerCredencial(boxId);
  if (!cred?.refresh) return null;
  try {
    const r = await fetch(`${API}/api/auth/tpv-refresh`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh: cred.refresh }),
    });
    const d = await r.json().catch(() => ({}));
    return r.ok && d.token ? d.token : null;
  } catch { return null; }
}

async function tokenPorPin(boxId, pin, slug) {
  const r = await fetch(`${API}/api/auth/tpv-open`, {
    method: 'POST', headers: { 'Content-Type': 'application/json', ...(slug ? { 'X-Org-Slug': slug } : {}) },
    body: JSON.stringify({ box_id: boxId, pin }),
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data.error || 'No se pudo entrar con ese PIN');
  guardarBoxInfo({ boxId, boxName: data.session?.boxName, eventName: data.session?.eventName });
  return data.token;
}

// Sube la cola de una caja con un token ya conseguido. Las ventas llevan
// client_sale_id, así que reintentar no duplica nada.
async function subirCola(boxId, token) {
  const hdrs = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  const ventas = leerJSON(QUEUE_PREFIX + boxId, []) || [];
  const quedan = [];
  let subidas = 0, rechazadas = 0;
  for (const sale of ventas) {
    try {
      const rr = await fetch(`${API}/api/tpv/sales`, { method: 'POST', headers: hdrs, body: JSON.stringify(sale) });
      if (rr.ok) { subidas++; continue; }
      // La percha ya la tomó otra caja: reintentar siempre va a fallar.
      if (rr.status === 409) {
        const d = await rr.json().catch(() => ({}));
        if (d.code === 'HANGER_TAKEN') { rechazadas++; continue; }
      }
      quedan.push(sale);
    } catch { quedan.push(sale); }
  }
  guardarJSON(QUEUE_PREFIX + boxId, quedan);

  const devs = leerJSON(RET_PREFIX + boxId, []) || [];
  const devsQuedan = [];
  for (const it of devs) {
    try {
      const rr = await fetch(`${API}/api/tpv/hangers/${it.n}/return`, { method: 'POST', headers: hdrs });
      if (!rr.ok && rr.status !== 400) devsQuedan.push(it);   // 400 = ya estaba devuelta
    } catch { devsQuedan.push(it); }
  }
  guardarJSON(RET_PREFIX + boxId, devsQuedan);

  return { subidas, rechazadas, quedan: quedan.length };
}

// Resume el resultado en una línea para mostrar en pantalla.
function resumenSubida(res) {
  const partes = [`Se subieron ${res.subidas} ${res.subidas === 1 ? 'venta' : 'ventas'}`];
  if (res.rechazadas) partes.push(`${res.rechazadas} rechazadas (la percha ya estaba ocupada)`);
  if (res.quedan) partes.push(`${res.quedan} no pudieron subir: probá de nuevo`);
  return partes.join(' · ') + (res.quedan ? '' : '. Ya figuran en el panel.');
}
// Al desvincular el boliche no puede quedar ninguna credencial: la tablet pasa a otras manos.
function olvidarCredenciales() {
  try {
    Object.keys(localStorage).filter(k => k.startsWith(CRED_PREFIX)).forEach(k => localStorage.removeItem(k));
  } catch { /* noop */ }
}

export default function Tpv() {
  const [session, setSession] = useState(() => {
    try { return JSON.parse(localStorage.getItem(STORE_KEY) || 'null'); } catch { return null; }
  });
  const [link, setLink] = useState(() => {
    try { return JSON.parse(localStorage.getItem(LINK_KEY) || 'null'); } catch { return null; }
  });

  const onOpen = (data) => { localStorage.setItem(STORE_KEY, JSON.stringify(data)); setSession(data); };
  const onClose = () => { localStorage.removeItem(STORE_KEY); setSession(null); };
  const onLink = (l) => { localStorage.setItem(LINK_KEY, JSON.stringify(l)); setLink(l); };
  const onUnlink = () => { localStorage.removeItem(LINK_KEY); localStorage.removeItem(STORE_KEY); olvidarCredenciales(); setLink(null); setSession(null); };

  // TODO dispositivo (app y web) se vincula a un boliche por login antes de operar: así
  // siempre se ve la cuenta conectada y se puede cambiar. Ninguno cae a un boliche por defecto.
  const needLink = !link;

  // Un error de render dentro de la caja dejaba la pantalla en blanco y había que
  // cerrar y volver a abrir la caja (lo que pasó el viernes 24). El boundary la deja
  // recuperable sin perder la sesión ni la cola de ventas, y avisa a la caja negra.
  const onCajaError = (err) => {
    if (!session) return;
    reportTpvError({
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.token}` },
      eventId: session.eventId,
      kind: 'render_crash',
      message: 'la pantalla de la caja se corto: ' + (err?.message || err),
      route: '/tpv',
      context: { screen: 'caja' },
    });
  };

  return (
    <>
      <InstallPrompt />
      {session ? (
        <ErrorBoundary
          title="Se cortó la pantalla de la caja"
          hint='Las ventas ya cobradas están guardadas (incluso las que faltan sincronizar). Tocá "Reintentar" para volver a la caja sin cerrar el turno.'
          onError={onCajaError}
        >
          <TpvCaja session={session} onClose={onClose} onSession={onOpen} />
        </ErrorBoundary>
      ) : needLink ? <TpvLink onLink={onLink} />
        : <TpvLogin onOpen={onOpen} link={link} onUnlink={onUnlink} />}
    </>
  );
}

/* ---------------- VINCULAR BOLICHE (solo app) ---------------- */
function TpvLink({ onLink }) {
  const [slug, setSlug] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);

  async function link() {
    setErr(''); setLoading(true);
    try {
      const r = await fetch(`${API}/api/auth/tpv-link`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ slug: slug.trim().toLowerCase(), email, password }),
      });
      const data = await r.json();
      if (!r.ok) { setErr(data.error || 'No se pudo vincular'); return; }
      onLink({ token: data.token, slug: data.org.slug, name: data.org.name });
    } catch { setErr('Error de conexión'); } finally { setLoading(false); }
  }

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ width: '100%', maxWidth: 420 }}>
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <img src="/assets/tukipass-icon.png" alt="" style={{ height: 44, marginBottom: 12 }} />
          <div className="lb-tracked" style={{ letterSpacing: '0.42em', color: 'var(--ink-700)' }}>— VINCULAR BOLICHE —</div>
        </div>
        <div className="lb-card" style={{ padding: '28px 26px', display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ fontSize: 12.5, color: 'var(--ink-700)', lineHeight: 1.5 }}>Este dispositivo todavía no está vinculado a ningún boliche. Ingresá con la cuenta del boliche (dueño o administrador). Se hace una sola vez por dispositivo.</div>
          <Field label="Código del boliche"><input className="lb-input" value={slug} onChange={e => setSlug(e.target.value)} placeholder="ej: tu-boliche" autoCapitalize="none" /></Field>
          <Field label="Email"><input className="lb-input" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="cuenta@boliche.com" autoCapitalize="none" /></Field>
          <Field label="Contraseña"><input className="lb-input" type="password" value={password} onChange={e => setPassword(e.target.value)} onKeyDown={e => e.key === 'Enter' && link()} /></Field>
          {err && <div className="lb-err">{err}</div>}
          <button className="lb-btn lb-btn--primary lb-btn--block" onClick={link} disabled={!slug || !email || !password || loading}>{loading ? 'Vinculando…' : 'Vincular →'}</button>
        </div>
        <div style={{ textAlign: 'center', fontSize: 11, color: 'var(--ink-600)', marginTop: 18 }}>Tu código de boliche es el que aparece en tu web: <b>código</b>.tukipro.com</div>
      </div>
    </div>
  );
}

/* Sugerencia para instalar la app. Usa el evento capturado temprano en index.html
   (window.__bipEvent). Si no está disponible, muestra instrucciones del navegador. */
function InstallPrompt() {
  const [evt, setEvt] = useState(typeof window !== 'undefined' ? window.__bipEvent : null);
  const [dismissed, setDismissed] = useState(false);
  const [hint, setHint] = useState('');

  useEffect(() => {
    const h = () => setEvt(window.__bipEvent);
    window.addEventListener('tk-can-install', h);
    window.addEventListener('appinstalled', () => setDismissed(true));
    return () => window.removeEventListener('tk-can-install', h);
  }, []);

  const standalone = typeof window !== 'undefined' && (window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone);
  const isIOS = typeof navigator !== 'undefined' && /iphone|ipad|ipod/i.test(navigator.userAgent);
  // En la app nativa ya está instalada: nunca mostrar la sugerencia de instalar la PWA.
  if (nativePrinterAvailable() || standalone || dismissed) return null;

  const install = async () => {
    const e = window.__bipEvent;
    if (e) {
      e.prompt();
      try { await e.userChoice; } catch {}
      window.__bipEvent = null; setEvt(null); setDismissed(true);
    } else if (isIOS) {
      setHint('En Safari: tocá el botón Compartir (cuadrado con flecha) y elegí "Agregar a inicio".');
    } else {
      setHint('En Chrome: tocá el menú ⋮ (arriba a la derecha) y elegí "Instalar app" o "Agregar a la pantalla principal". Si no aparece, recargá la página una vez con internet.');
    }
  };

  return (
    <div style={{ position: 'fixed', bottom: 16, left: 16, right: 16, zIndex: 300, display: 'flex', justifyContent: 'center', pointerEvents: 'none' }}>
      <div style={{ pointerEvents: 'auto', maxWidth: 540, width: '100%', background: 'var(--ink-100)', border: '1px solid var(--red-700)', borderRadius: 6, padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 12, boxShadow: '0 12px 40px -12px rgba(0,0,0,0.8)' }}>
        <img src="/assets/tukipass-icon.png" alt="" style={{ height: 30, flex: 'none' }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ color: '#fff', fontWeight: 700, fontSize: 13 }}>Instalar TPV en la tablet</div>
          <div style={{ color: 'var(--ink-700)', fontSize: 12 }}>{hint || 'Se abre como app y funciona sin internet.'}</div>
        </div>
        <button className="lb-btn lb-btn--primary lb-btn--sm" onClick={install}>Instalar</button>
        <button onClick={() => setDismissed(true)} style={{ background: 'transparent', border: 0, color: 'var(--ink-600)', cursor: 'pointer', fontSize: 18, flex: 'none' }}>✕</button>
      </div>
    </div>
  );
}

/* ---------------- LOGIN ---------------- */
function TpvLogin({ onOpen, link, onUnlink }) {
  const [events, setEvents] = useState([]);
  const [boxes, setBoxes] = useState([]);
  const [eventId, setEventId] = useState('');
  const [boxId, setBoxId] = useState('');
  const [pin, setPin] = useState('');
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);

  // App vinculada → lista SUS eventos/cajas con el token del dispositivo. Web → por subdominio.
  const linkHeaders = link ? { Authorization: `Bearer ${link.token}`, 'X-Org-Slug': link.slug } : {};
  const eventsUrl = link ? `${API}/api/tpv/my-events` : `${API}/api/tpv/public/events`;
  const boxesUrl = (eid) => link ? `${API}/api/tpv/my-boxes?event_id=${eid}` : `${API}/api/tpv/public/boxes?event_id=${eid}`;

  // ANTES cualquier fallo terminaba en una lista vacía, en silencio: token vencido,
  // cuenta sin permiso, servidor caído o sin señal se veían todos igual — "no hay
  // eventos" — y desde la caja era imposible saber qué estaba pasando. Un vínculo
  // vencido dejaba la tablet mirando una pantalla vacía para siempre.
  const [cargaErr, setCargaErr] = useState('');
  const [vinculoVencido, setVinculoVencido] = useState(false);
  // Cuándo se guardaron las listas que se están mostrando (0 = vinieron del servidor).
  const [desdeCache, setDesdeCache] = useState(0);
  // Ventas que quedaron guardadas en la tablet y nunca llegaron al servidor.
  const [pend, setPend] = useState(() => colasPendientes());
  const [rec, setRec] = useState(null);   // { boxId, pin, loading, err, hecho }
  const [auto, setAuto] = useState({});   // boxId → 'subiendo' | resumen de lo subido
  const autoRef = useRef({});             // para no reintentar en bucle en esta pantalla

  // Al entrar al TPV, con internet, las colas que quedaron se suben SOLAS: no hace
  // falta elegir evento ni abrir la caja. Solo se le pide el PIN al operador cuando la
  // tablet no tiene permiso guardado de esa caja (pasa con las que nunca abrieron una
  // caja con la versión nueva), porque sin permiso el servidor no acepta las ventas.
  useEffect(() => {
    let vivo = true;
    const correr = async () => {
      if (!navigator.onLine) return;
      for (const p of colasPendientes()) {
        if (autoRef.current[p.boxId]) continue;
        const token = await tokenPorRefresh(p.boxId);
        if (!token) { autoRef.current[p.boxId] = 'sin-permiso'; continue; }
        autoRef.current[p.boxId] = 'hecho';
        if (!vivo) return;
        setAuto(a => ({ ...a, [p.boxId]: 'subiendo' }));
        try {
          const res = await subirCola(p.boxId, token);
          if (!vivo) return;
          // La fila se queda con el resultado a la vista: si la sacáramos al vaciarse
          // la cola, el operador nunca vería que se subieron (y volvería a buscarlas).
          setAuto(a => ({ ...a, [p.boxId]: resumenSubida(res) }));
        } catch {
          if (vivo) setAuto(a => ({ ...a, [p.boxId]: '' }));   // queda el camino del PIN
        }
      }
    };
    correr();
    window.addEventListener('online', correr);
    return () => { vivo = false; window.removeEventListener('online', correr); };
  }, []);

  useEffect(() => {
    let vivo = true;
    (async () => {
      try {
        const r = await fetch(eventsUrl, { headers: linkHeaders });
        if (!vivo) return;
        if (r.status === 401 || r.status === 403) {
          // El vínculo con el boliche ya no sirve. Se dice y se ofrece rehacerlo: sin
          // esto no hay forma de salir desde la tablet.
          setVinculoVencido(true);
          setCargaErr(link
            ? 'El vínculo con el boliche venció. Volvé a vincular la tablet para seguir.'
            : 'Esta tablet no está vinculada a ningún boliche.');
          return;
        }
        const d = await r.json();
        if (!vivo) return;
        if (Array.isArray(d)) { setEvents(d); setCargaErr(''); setDesdeCache(0); guardarJSON(CACHE_EVENTS_KEY, { at: Date.now(), list: d }); }
        else setCargaErr(d?.error || 'No se pudieron cargar los eventos.');
      } catch {
        if (!vivo) return;
        // Sin señal: la tablet muestra los eventos que vio la última vez que hubo
        // internet. Lo nuevo (un evento creado hoy) no aparece, y eso está dicho.
        const c = leerJSON(CACHE_EVENTS_KEY);
        if (c && Array.isArray(c.list) && c.list.length) { setEvents(c.list); setDesdeCache(c.at || 1); setCargaErr(''); }
        else setCargaErr('Sin conexión con el servidor y esta tablet no tiene eventos guardados.');
      }
    })();
    return () => { vivo = false; };
  }, []); // eslint-disable-line

  useEffect(() => {
    if (!eventId) { setBoxes([]); return; }
    let vivo = true;
    (async () => {
      try {
        const r = await fetch(boxesUrl(eventId), { headers: linkHeaders });
        const d = await r.json();
        if (!vivo) return;
        if (Array.isArray(d)) { setBoxes(d); setCargaErr(''); guardarJSON(cacheBoxesKey(eventId), { at: Date.now(), list: d }); }
        else setCargaErr(d?.error || 'No se pudieron cargar las cajas.');
      } catch {
        if (!vivo) return;
        const c = leerJSON(cacheBoxesKey(eventId));
        if (c && Array.isArray(c.list) && c.list.length) { setBoxes(c.list); setDesdeCache(c.at || 1); setCargaErr(''); }
        else setCargaErr('Sin conexión y esta tablet no tiene las cajas de ese evento guardadas.');
      }
    })();
    return () => { vivo = false; };
  }, [eventId]); // eslint-disable-line

  async function open() {
    setErr(''); setLoading(true);
    try {
      if (navigator.onLine) {
        try {
          const r = await fetch(`${API}/api/auth/tpv-open`, {
            method: 'POST', headers: { 'Content-Type': 'application/json', ...(link ? { 'X-Org-Slug': link.slug } : {}) },
            body: JSON.stringify({ event_id: eventId, box_id: boxId, pin }),
          });
          const data = await r.json();
          // El servidor contestó: su palabra manda. Un PIN rechazado acá NO se
          // reintenta contra lo guardado (el dueño pudo haberlo cambiado hoy).
          if (!r.ok) { setErr(data.error || 'No se pudo abrir la caja'); return; }
          await guardarCredencial({ eventId, boxId, pin, token: data.token, refresh: data.refresh, session: data.session });
          guardarBoxInfo({ boxId, boxName: data.session?.boxName, eventName: data.session?.eventName });
          onOpen({ token: data.token, refresh: data.refresh || null, ...data.session });
          return;
        } catch { /* no hubo respuesta: seguimos con lo guardado en la tablet */ }
      }
      await abrirGuardada();
    } finally { setLoading(false); }
  }

  // Rescate de las ventas que quedaron en la tablet. No abre la caja: entra con el PIN,
  // sube lo pendiente y vuelve. Reintentar no duplica (cada venta lleva client_sale_id).
  async function subir(bid) {
    setRec(r => ({ ...r, loading: true, err: '' }));
    try {
      const token = await tokenPorPin(bid, rec.pin, link?.slug);
      const res = await subirCola(bid, token);
      setRec(r => ({ ...r, loading: false, hecho: resumenSubida(res) }));
    } catch (e) {
      setRec(r => ({ ...r, loading: false, err: e.message || 'No se pudo subir' }));
    }
  }

  // Apertura sin internet: el PIN se compara contra el verificador que quedó en la
  // tablet la última vez que ESTA caja se abrió con señal.
  async function abrirGuardada() {
    const cred = leerCredencial(boxId);
    if (!cred) {
      setErr('Sin internet solo se pueden abrir cajas que ya se abrieron en esta tablet. Esta todavía no, así que la primera apertura necesita conexión.');
      return;
    }
    const resta = (cred.lockUntil || 0) - Date.now();
    if (resta > 0) { setErr(`Demasiados intentos. Probá de nuevo en ${Math.ceil(resta / 1000)} segundos.`); return; }
    const hash = await hashPin(pin, cred.salt, cred.iters);
    if (!hash) { setErr('Esta tablet no puede validar el PIN sin internet. Abrí la caja con conexión.'); return; }
    if (hash !== cred.hash) {
      // Si el bloqueo anterior ya venció, el contador arranca de cero.
      const previos = cred.lockUntil && cred.lockUntil < Date.now() ? 0 : (cred.fails || 0);
      const fails = previos + 1;
      guardarJSON(CRED_PREFIX + boxId, { ...cred, fails, lockUntil: fails >= MAX_INTENTOS ? Date.now() + BLOQUEO_MS : 0 });
      setErr(fails >= MAX_INTENTOS ? 'PIN incorrecto. Esperá un minuto antes de volver a probar.' : 'PIN incorrecto');
      return;
    }
    guardarJSON(CRED_PREFIX + boxId, { ...cred, fails: 0, lockUntil: 0 });
    // El token guardado puede estar vencido: no importa para vender (todo va a la cola).
    // Cuando vuelva internet, la caja lo renueva sola con el token de refresco.
    onOpen({ ...cred.session, token: cred.token, refresh: cred.refresh, offlineOpen: true });
  }

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ width: '100%', maxWidth: 420 }}>
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <img src="/assets/tukipass-icon.png" alt="" style={{ height: 44, marginBottom: 12 }} />
          <div className="lb-tracked" style={{ letterSpacing: '0.42em', color: 'var(--ink-700)' }}>— TPV · BARRA —</div>
        </div>
        {/* Ventas cobradas que nunca llegaron al servidor. Va ANTES del formulario y en
            rojo: mientras esto esté, en el panel esa caja figura vendiendo de menos. */}
        {pend.length > 0 && (
          <div style={{ border: '1px solid var(--red-500)', background: 'rgba(200,16,46,0.10)', borderRadius: 6, padding: '14px 16px', marginBottom: 16 }}>
            <div style={{ color: '#fff', fontWeight: 700, fontSize: 13, marginBottom: 4 }}>Hay ventas sin subir en esta tablet</div>
            <div style={{ fontSize: 12, color: 'var(--ink-700)', lineHeight: 1.5, marginBottom: 12 }}>
              Se cobraron sin internet y todavía no llegaron al sistema, así que en el panel esa caja aparece vendiendo de menos. Con internet suben solas; si esta tablet no tiene permiso guardado de la caja, se sube poniendo su PIN. No hace falta que el evento siga abierto.
            </div>
            {pend.map(p => (
              <div key={p.boxId} style={{ borderTop: '1px solid var(--border)', paddingTop: 10, marginTop: 10 }}>
                <div style={{ fontSize: 12.5, color: '#fff' }}>
                  {p.boxName || 'Caja'}{p.eventName ? ` · ${p.eventName}` : ''}
                </div>
                <div style={{ fontSize: 12, color: 'var(--ink-700)', marginBottom: 8 }}>
                  {p.ventas} {p.ventas === 1 ? 'venta' : 'ventas'}{p.total ? ` · ${money(p.total)}` : ''}
                  {p.devoluciones ? ` · ${p.devoluciones} ${p.devoluciones === 1 ? 'devolución' : 'devoluciones'} de percha` : ''}
                </div>
                {auto[p.boxId] === 'subiendo' ? (
                  <div style={{ fontSize: 12, color: 'var(--ink-700)' }}>Subiendo solas…</div>
                ) : auto[p.boxId] ? (
                  <div style={{ fontSize: 12, color: '#2ecc71' }}>{auto[p.boxId]}</div>
                ) : rec?.boxId === p.boxId && rec.hecho ? (
                  <div style={{ fontSize: 12, color: '#2ecc71' }}>{rec.hecho}</div>
                ) : rec?.boxId === p.boxId ? (
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    <input className="lb-input" inputMode="numeric" maxLength={4} autoFocus
                      value={rec.pin} onChange={e => setRec({ ...rec, pin: e.target.value.replace(/\D/g, '').slice(0, 4), err: '' })}
                      placeholder="PIN" style={{ width: 110, fontSize: 18, textAlign: 'center', letterSpacing: '0.3em' }} />
                    <button className="lb-btn lb-btn--primary lb-btn--sm" disabled={rec.pin.length !== 4 || rec.loading}
                      onClick={() => subir(p.boxId)}>{rec.loading ? 'Subiendo…' : 'Subir ahora'}</button>
                    <button className="lb-btn lb-btn--ghost lb-btn--sm" onClick={() => setRec(null)} disabled={rec.loading}>Cancelar</button>
                    {rec.err && <div className="lb-err" style={{ width: '100%' }}>{rec.err}</div>}
                  </div>
                ) : (
                  <button className="lb-btn lb-btn--primary lb-btn--sm" onClick={() => setRec({ boxId: p.boxId, pin: '', loading: false, err: '', hecho: '' })}>
                    Subir estas ventas →
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
        <div className="lb-card" style={{ padding: '28px 26px', display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* El motivo va ARRIBA de todo: es lo primero que hay que ver cuando la lista
              no trae nada. Antes la pantalla se veía igual con el vínculo vencido que
              sin eventos cargados, y desde la caja no había forma de distinguirlo. */}
          {cargaErr && (
            <div style={{ border: '1px solid ' + (vinculoVencido ? 'var(--red-500)' : 'var(--border)'), background: vinculoVencido ? 'rgba(200,16,46,0.10)' : 'var(--ink-050)', borderRadius: 6, padding: '12px 14px', fontSize: 13, color: 'var(--ink-800)', lineHeight: 1.5 }}>
              {cargaErr}
              {vinculoVencido && onUnlink && (
                <button className="lb-btn lb-btn--primary lb-btn--sm lb-btn--block" style={{ marginTop: 12 }} onClick={onUnlink}>
                  Vincular la tablet de nuevo
                </button>
              )}
            </div>
          )}
          <Field label="Evento">
            <select className="lb-select" value={eventId} onChange={e => { setEventId(e.target.value); setBoxId(''); }}>
              <option value="">{events.length === 0 && !cargaErr ? 'No hay eventos cargados' : 'Elegí el evento'}</option>
              {events.map(ev => <option key={ev.id} value={ev.id}>{ev.name}</option>)}
            </select>
          </Field>
          <Field label="Caja">
            <select className="lb-select" value={boxId} onChange={e => setBoxId(e.target.value)} disabled={!eventId}>
              <option value="">Elegí la caja</option>
              {boxes.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </Field>
          {/* Sin señal la tablet muestra lo último que vio. Decirlo evita el susto de
              "me faltan cajas" y aclara por qué una caja nueva no está. */}
          {desdeCache > 0 && (
            <div style={{ border: '1px solid var(--border)', background: 'var(--ink-050)', borderRadius: 6, padding: '10px 12px', fontSize: 12, color: 'var(--ink-700)', lineHeight: 1.5 }}>
              Sin internet · lista guardada el {new Date(desdeCache).toLocaleString('es-AR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}. Lo que se haya creado después no aparece.
              {boxId && (leerCredencial(boxId)
                ? ' Esta caja ya se abrió en esta tablet: entrá con su PIN.'
                : ' Esta caja nunca se abrió acá, así que la primera apertura necesita conexión.')}
            </div>
          )}
          <Field label="PIN (4 dígitos)">
            <input className="lb-input" inputMode="numeric" maxLength={4} value={pin}
              onChange={e => setPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
              placeholder="● ● ● ●" style={{ fontSize: 22, textAlign: 'center', letterSpacing: '0.4em' }} />
          </Field>
          {err && <div className="lb-err">{err}</div>}
          <button className="lb-btn lb-btn--primary lb-btn--block" onClick={open} disabled={!eventId || !boxId || pin.length !== 4 || loading}>
            {loading ? 'Abriendo…' : 'Abrir caja →'}
          </button>
        </div>
        <div style={{ textAlign: 'center', fontSize: 11, color: 'var(--ink-600)', marginTop: 18 }}>
          Funciona sin internet · las ventas quedan guardadas en la tablet
        </div>
        {link && (
          <div style={{ marginTop: 16, padding: '12px 14px', background: 'var(--ink-050)', border: '1px solid var(--border)', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
            <div style={{ fontSize: 12, color: 'var(--ink-700)' }}>Conectado a <b style={{ color: '#fff' }}>{link.name}</b></div>
            <button className="lb-btn lb-btn--ghost lb-btn--sm" onClick={() => { if (window.confirm(`¿Desvincular este dispositivo de ${link.name}? Vas a poder vincular otro boliche.`)) onUnlink(); }}>Cambiar boliche</button>
          </div>
        )}
      </div>
    </div>
  );
}

/* ---------------- CAJA ABIERTA ---------------- */
function TpvCaja({ session, onClose, onSession }) {
  const [products, setProducts] = useState([]);
  const [ticket, setTicket] = useState([]); // {id,name,price,qty}
  const [note, setNote] = useState('');
  const [serverSold, setServerSold] = useState(0);
  const [serverCount, setServerCount] = useState(0);
  const [online, setOnline] = useState(navigator.onLine);
  const [pending, setPending] = useState(0);
  // Espejos para que el latido los lea sin re-crear el intervalo cada vez que cambian.
  const pendingRef = useRef(0);
  const soldCountRef = useRef(0);
  const [printer, setPrinter] = useState(null); // BLE: { device, characteristic }
  const NATIVE = nativePrinterAvailable();      // app Capacitor → Bluetooth clásico (SPP)
  const printerKey = `tk_tpv_printer_${session.boxId}`;
  const [nativeAddr, setNativeAddr] = useState(() => { try { return localStorage.getItem(printerKey) || null; } catch { return null; } });
  const [nativeName, setNativeName] = useState(() => { try { return localStorage.getItem(printerKey + '_name') || ''; } catch { return ''; } });
  const [picker, setPicker] = useState(null);   // { loading, devices } | null
  const [calcOpen, setCalcOpen] = useState(false);
  const [cat, setCat] = useState(null); // categoría seleccionada (tabs horizontales)
  const isGuarda = session.menuType === 'guardarropa';
  const isBoleteria = session.menuType === 'boleteria';
  const queueKey = `tk_tpv_queue_${session.boxId}`;
  // Cola aparte para las DEVOLUCIONES de percha: si se cae la red a la hora del
  // retiro, la prenda se entrega igual y la devolucion sube despues.
  const retQueueKey = `tk_tpv_ret_${session.boxId}`;
  const [bolDiag, setBolDiag] = useState(''); // diagnóstico de la carga de lotes (boletería)
  const [birthdayGuests, setBirthdayGuests] = useState([]); // cumpleañeros pendientes (boletería)
  const [bdPicker, setBdPicker] = useState(null); // lote de cumpleaños abierto en el selector, o null
  const [bdSearch, setBdSearch] = useState('');
  const isBarra = !isGuarda && !isBoleteria;
  const [mesaCredits, setMesaCredits] = useState([]); // saldos de mesa de esta caja (cache offline)
  const [mesaApplied, setMesaApplied] = useState(null); // { code, remaining, mesa_name } aplicado al ticket
  const [cobrando, setCobrando] = useState(false);   // bloquea el boton mientras cobra
  const cobrandoRef = useRef(false);                 // frena dos toques en el mismo instante
  const [queuedTotal, setQueuedTotal] = useState(0);  // monto de las ventas sin sincronizar
  const syncingRef = useRef(false);                  // un solo sync a la vez
  const retSyncingRef = useRef(false);               // idem para las devoluciones
  const retPendingRef = useRef(0);                   // espejo del contador de devoluciones
  const [retPending, setRetPending] = useState(0);   // devoluciones sin sincronizar
  // Ventas que el servidor rechazo porque la percha ya estaba ocupada (dos puestos
  // offline sobre el mismo numero). No se pueden reintentar: hay que rehacerlas.
  const [hangerConflicts, setHangerConflicts] = useState([]);
  const [mesaModal, setMesaModal] = useState(false);
  const [mesaCodeInput, setMesaCodeInput] = useState('');   // valor real (en mayúsculas)
  const [mesaReveal, setMesaReveal] = useState(false);      // mostrar el último carácter un instante
  const mesaRevealT = useRef(null);
  const mesaCacheKey = `tk_tpv_mesa_${session.boxId}`;
  // ── Guardarropa por perchas ──
  // El estado del perchero se guarda en disco: si se cae la red, el guardarropa tiene
  // que seguir sabiendo qué perchas tienen prenda (es lo único que evita entregar dos
  // veces el mismo número).
  const hangerCacheKey = `tk_tpv_hangers_${session.eventId}`;
  const [hangers, setHangers] = useState(() => {
    try { return JSON.parse(localStorage.getItem(hangerCacheKey) || 'null') || { from: 1, to: 50, ocupadas: [] }; }
    catch { return { from: 1, to: 50, ocupadas: [] }; }
  });
  const [hangerPick, setHangerPick] = useState(null);   // { n, extra } percha elegida, antes de cobrar
  const [hangerBusy, setHangerBusy] = useState(null);   // { n, oc } percha ocupada tocada → acciones
  const [printModal, setPrintModal] = useState(null);   // { sale } → los dos botones de impresión
  // Input tipo contraseña: se ve el último carácter recién tipeado y enseguida pasa a ●, así
  // quien está atrás en la fila no ve el código. Fuerza mayúsculas (los códigos son en mayúsculas).
  const mesaMasked = mesaCodeInput ? ('●'.repeat(Math.max(0, mesaCodeInput.length - 1)) + (mesaReveal ? mesaCodeInput.slice(-1) : '●')) : '';
  const onMesaCode = (e) => {
    const v = e.target.value.toUpperCase();
    const prevLen = mesaCodeInput.length;
    if (v.length > prevLen) {
      setMesaCodeInput(mesaCodeInput + v.slice(prevLen));
      setMesaReveal(true); clearTimeout(mesaRevealT.current); mesaRevealT.current = setTimeout(() => setMesaReveal(false), 800);
    } else if (v.length < prevLen) {
      setMesaCodeInput(mesaCodeInput.slice(0, v.length)); setMesaReveal(false);
    }
  };

  const authHeaders = { 'Content-Type': 'application/json', Authorization: `Bearer ${session.token}` };

  // ── Token vencido tras abrir sin internet ──
  // Al reabrir la caja sin señal se reusa el token guardado, que puede estar vencido.
  // Cuando vuelve internet hay que renovarlo ANTES de subir la cola; si no, las ventas
  // rebotarían con 401 y quedarían dando vueltas toda la noche. Devuelve el token a usar.
  const ensureToken = useCallback(async () => {
    if (!session.refresh || !navigator.onLine) return session.token;
    let exp = 0;
    try { exp = (JSON.parse(atob(String(session.token).split('.')[1])).exp || 0) * 1000; } catch { exp = 0; }
    if (exp && exp - Date.now() > 10 * 60 * 1000) return session.token;   // todavía sirve
    try {
      const r = await fetch(`${API}/api/auth/tpv-refresh`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh: session.refresh }),
      });
      const d = await r.json();
      if (!r.ok || !d.token) return session.token;
      onSession?.({ ...session, ...d.session, token: d.token, refresh: session.refresh, offlineOpen: false });
      return d.token;
    } catch { return session.token; }
  }, [session, onSession]);

  // Barra: descarga los saldos de mesa asignados a esta caja (para descontar offline).
  const loadMesaCredits = useCallback(() => {
    fetch(`${API}/api/tpv/mesa-credits`, { headers: authHeaders })
      .then(r => r.json())
      .then(list => { const arr = Array.isArray(list) ? list : []; setMesaCredits(arr); try { localStorage.setItem(mesaCacheKey, JSON.stringify(arr)); } catch {} })
      .catch(() => { try { setMesaCredits(JSON.parse(localStorage.getItem(mesaCacheKey) || '[]')); } catch { setMesaCredits([]); } });
  }, [mesaCacheKey]); // eslint-disable-line

  // Estado del perchero. Se refresca al abrir y cada 20s (para ver lo que devolvió
  // otra caja), pero SIEMPRE con respaldo en disco: sin red se usa lo último guardado.
  const loadHangers = useCallback(() => {
    if (!isGuarda) return;
    fetch(`${API}/api/tpv/hangers`, { headers: authHeaders })
      .then(r => (r.ok ? r.json() : Promise.reject(r)))
      .then(d => {
        setHangers(d);
        try { localStorage.setItem(hangerCacheKey, JSON.stringify(d)); } catch {}
      })
      .catch(() => { /* sin red: queda el cache que ya está en el estado */ });
  }, [isGuarda, hangerCacheKey]); // eslint-disable-line
  useEffect(() => {
    if (!isGuarda) return undefined;
    loadHangers();
    const t = setInterval(loadHangers, 20000);
    return () => clearInterval(t);
  }, [isGuarda, loadHangers]);

  // Devolver la prenda: libera la percha. NO anula la venta (lo cobrado sigue
  // contando). Offline no se puede: hay que registrarlo en el servidor.
  // ── Cola de DEVOLUCIONES ──
  // Caso real de la noche: se cobran 50 perchas temprano con buena red, y a las 5 de
  // la mañana —cuando todos retiran y todos están con el celular— la red se degrada.
  // Sin esta cola, esas devoluciones fallaban: la prenda se entregaba igual, pero la
  // percha quedaba ocupada en el sistema, no se podía reasignar, y al cierre aparecía
  // como "sin retirar" cuando ya se había retirado.
  const loadRetQueue = useCallback(() => {
    try { const a = JSON.parse(localStorage.getItem(retQueueKey) || '[]'); return Array.isArray(a) ? a : []; }
    catch { return []; }
  }, [retQueueKey]);
  const saveRetQueue = useCallback((q) => {
    try { localStorage.setItem(retQueueKey, JSON.stringify(q)); } catch {}
    setRetPending(q.length);
  }, [retQueueKey]);

  async function devolverPercha(n) {
    // Caso que SÍ pasa en la noche: se cobró sin red (o el servidor no respondió), la
    // venta quedó en la cola local, y el cliente vuelve a retirar su prenda. El
    // servidor todavía no conoce esa percha, así que preguntarle daba "esa percha no
    // está ocupada" y el operador no podía entregar el abrigo.
    // Solución: si la venta está en la cola, la devolución se marca AHÍ. Cuando la
    // venta sincronice viajará con la devolución puesta, y la percha entra al
    // servidor ya libre (el índice único solo cuenta las no devueltas).
    const q = loadQueue();
    const idx = q.findIndex(v => Number(v.hanger_number) === Number(n) && !v.hanger_returned_at);
    if (idx >= 0) {
      q[idx] = { ...q[idx], hanger_returned_at: new Date().toISOString() };
      saveQueue(q);
      setHangers(h => ({ ...(h || {}), ocupadas: ((h && h.ocupadas) || []).filter(o => Number(o.n) !== Number(n)) }));
      setHangerBusy(null);
      return;
    }
    // La percha ya está en el servidor. Se libera SIEMPRE en la pantalla (la prenda se
    // entrega igual) y, si no se pudo avisar al servidor, la devolución se encola.
    const liberarLocal = () => {
      setHangers(h => ({ ...(h || {}), ocupadas: ((h && h.ocupadas) || []).filter(o => Number(o.n) !== Number(n)) }));
      setHangerBusy(null);
    };
    const encolar = () => {
      const q = loadRetQueue();
      if (!q.some(x => Number(x.n) === Number(n))) q.push({ n: Number(n), at: new Date().toISOString() });
      saveRetQueue(q);
      liberarLocal();
    };
    if (!navigator.onLine) { encolar(); return; }
    try {
      const ctrl = new AbortController();
      const timer = setTimeout(() => ctrl.abort(), SALE_TIMEOUT_MS);
      let r;
      try { r = await fetch(`${API}/api/tpv/hangers/${n}/return`, { method: 'POST', headers: authHeaders, signal: ctrl.signal }); }
      finally { clearTimeout(timer); }
      const d = await r.json().catch(() => ({}));
      // 400 = el servidor dice que esa percha no está ocupada: ya estaba devuelta, así
      // que la pantalla se pone al día. No se encola (no hay nada que reintentar).
      if (!r.ok && r.status === 400) { liberarLocal(); loadHangers(); return; }
      if (!r.ok) { encolar(); return; }
      liberarLocal();
      loadHangers();
    } catch {
      encolar();   // timeout o red caída: se entrega la prenda y se sincroniza después
    }
  }

  // Sube las devoluciones pendientes. Igual que la cola de ventas: una a la vez, con
  // timeout, y solo se saca de la cola lo que el servidor confirmó.
  const syncReturns = useCallback(async () => {
    if (retSyncingRef.current) return;
    const q = loadRetQueue();
    if (!q.length || !navigator.onLine) return;
    retSyncingRef.current = true;
    const hdrs = { 'Content-Type': 'application/json', Authorization: `Bearer ${await ensureToken()}` };
    const quedan = [];
    try {
      for (const it of q) {
        const ctrl = new AbortController();
        const timer = setTimeout(() => ctrl.abort(), SALE_TIMEOUT_MS);
        try {
          const r = await fetch(`${API}/api/tpv/hangers/${it.n}/return`, { method: 'POST', headers: hdrs, signal: ctrl.signal });
          // 400 = ya estaba devuelta en el servidor → objetivo cumplido, se descarta.
          if (!r.ok && r.status !== 400) quedan.push(it);
        } catch { quedan.push(it); }
        finally { clearTimeout(timer); }
        if (!navigator.onLine) { const i = q.indexOf(it); for (let k = i + 1; k < q.length; k++) quedan.push(q[k]); break; }
      }
    } finally { retSyncingRef.current = false; }
    saveRetQueue(quedan);
  }, [loadRetQueue, saveRetQueue, ensureToken]); // eslint-disable-line

  // Cumpleañeros pendientes del evento (para la caja de boletería).
  const loadBirthdayGuests = useCallback(() => {
    fetch(`${API}/api/tpv/birthdays`, { headers: authHeaders })
      .then(r => r.json())
      .then(g => setBirthdayGuests(Array.isArray(g) ? g : []))
      .catch(() => {});
  }, []); // eslint-disable-line

  // Boletería: carga los lotes de la caja (con cache offline + diagnóstico visible).
  const bolCacheKey = `tk_tpv_bol_${session.boxId}`;
  const loadBoleteriaLots = useCallback(() => {
    setBolDiag('cargando…');
    fetch(`${API}/api/tpv/boleteria-lots`, { headers: authHeaders })
      .then(async (r) => {
        let body = null; try { body = await r.json(); } catch { /* noop */ }
        if (!r.ok || !Array.isArray(body)) {
          setBolDiag(`No se pudo cargar (código ${r.status}${body && body.error ? ': ' + body.error : ''}). Usando lo guardado.`);
          try { setProducts(JSON.parse(localStorage.getItem(bolCacheKey) || '[]')); } catch { setProducts([]); }
          return;
        }
        // Las consumiciones van en su propia categoría: en la caja no son entradas, y
        // mezclarlas con los lotes lleva a venderlas por error como si dieran acceso.
        const mapped = body.map((l) => ({ id: l.id, name: l.name, price: Number(l.price), category: l.es_consumicion ? 'Consumiciones' : 'Entradas', is_birthday: !!l.is_birthday }));
        setProducts(mapped);
        setBolDiag(mapped.length ? '' : 'La caja no tiene lotes de boletería asignados (ni el evento tiene lotes con canal Boletería).');
        try { localStorage.setItem(bolCacheKey, JSON.stringify(mapped)); } catch { /* noop */ }
        if (mapped.some(m => m.is_birthday)) loadBirthdayGuests();
      })
      .catch((e) => {
        setBolDiag(`Sin conexión con el servidor (${e.message || 'error'}). Usando lo guardado.`);
        try { setProducts(JSON.parse(localStorage.getItem(bolCacheKey) || '[]')); } catch { setProducts([]); }
      });
  }, [bolCacheKey]); // eslint-disable-line

  const loadQueue = useCallback(() => { try { return JSON.parse(localStorage.getItem(queueKey) || '[]'); } catch { return []; } }, [queueKey]);

  // Perchas asignadas en ESTE dispositivo que todavía no se sincronizaron: cuentan
  // como ocupadas igual, o con la red caída se podría entregar dos veces el mismo
  // número.
  // OJO con el orden: va DESPUÉS de loadQueue. El array de dependencias de un
  // useCallback se evalúa al crearlo, así que si esto quedaba arriba, React tocaba
  // `loadQueue` antes de su declaración `const` y la pantalla entera reventaba con
  // "Cannot access before initialization".
  const hangersPendientes = useCallback(() => {
    try {
      return loadQueue()
        .filter(s => s.hanger_number != null)
        .map(s => ({ n: s.hanger_number, prenda: s.note || '', total: s.total, extra: !!s.hanger_extra }));
    } catch { return []; }
  }, [loadQueue]);
  // Guarda la cola y ADEMAS deja al dia los dos numeros que la UI necesita de ella
  // (cantidad y monto), asi el render no tiene que volver a leer localStorage.
  const saveQueue = useCallback((q) => {
    try { localStorage.setItem(queueKey, JSON.stringify(q)); } catch {}
    setPending(q.length);
    pendingRef.current = q.length;
    setQueuedTotal(q.reduce((a, s) => a + Number(s.total || 0), 0));
  }, [queueKey]);

  // productos. Boletería: los "productos" son los LOTES que vende la caja (con QR).
  // Barra/guardarropa: productos del menú (con cache offline).
  useEffect(() => {
    if (isBoleteria) { loadBoleteriaLots(); return; }
    if (!session.menuId) { setProducts([]); return; }
    const cacheKey = `tk_tpv_products_${session.menuId}`;
    fetch(`${API}/api/tpv/products?menu_id=${session.menuId}`, { headers: authHeaders })
      .then(r => r.json())
      .then(p => {
        const list = Array.isArray(p) ? p : [];
        setProducts(list);
        try { localStorage.setItem(cacheKey, JSON.stringify(list)); } catch {}
      })
      .catch(() => {
        // sin internet: usar la última lista cacheada
        try { setProducts(JSON.parse(localStorage.getItem(cacheKey) || '[]')); } catch { setProducts([]); }
      });
  }, []); // eslint-disable-line

  // barra: cargar los saldos de mesa asignados a esta caja al abrir
  useEffect(() => { if (isBarra) loadMesaCredits(); }, []); // eslint-disable-line

  // vendido en vivo (polling)
  const refreshSold = useCallback(() => {
    // Solo los dos números de ESTA caja, contados en la base. Antes se bajaban
    // todas las ventas del evento cada 5s para filtrarlas acá.
    fetch(`${API}/api/tpv/sales/summary?event_id=${session.eventId}&box_id=${session.boxId}`, { headers: authHeaders })
      .then(r => r.ok ? r.json() : Promise.reject(r))
      .then(s => { setServerSold(Number(s.total) || 0); setServerCount(s.count || 0); soldCountRef.current = s.count || 0; })
      .catch(() => {});
  }, []); // eslint-disable-line
  useEffect(() => { refreshSold(); const t = setInterval(refreshSold, 5000); return () => clearInterval(t); }, [refreshSold]);

  // sync offline
  const syncQueue = useCallback(async () => {
    // Un solo sync a la vez: este arranca por el evento 'online' Y por el intervalo,
    // así que sin el candado dos corridas simultáneas mandaban las mismas ventas dos
    // veces (ahora el client_sale_id lo haría inofensivo, pero igual es trabajo al
    // aire justo cuando la red está mal).
    if (syncingRef.current) return;
    const q = loadQueue();
    if (!q.length || !navigator.onLine) return;
    syncingRef.current = true;
    // Primero el token: si la caja se abrió sin internet, el guardado puede estar
    // vencido y TODA la cola rebotaría con 401.
    const hdrs = { 'Content-Type': 'application/json', Authorization: `Bearer ${await ensureToken()}` };
    const remaining = [];
    try {
      for (const sale of q) {
        // Timeout POR VENTA. Antes cada fetch podía quedarse colgado sin tope, y como
        // el bucle es secuencial una sola venta trabada bloqueaba toda la cola —
        // justo cuando la red está inestable, que es cuando esto corre.
        const ctrl = new AbortController();
        const timer = setTimeout(() => ctrl.abort(), SALE_TIMEOUT_MS);
        try {
          const r = await fetch(`${API}/api/tpv/sales`, { method: 'POST', headers: hdrs, body: JSON.stringify(sale), signal: ctrl.signal });
          if (!r.ok) {
            // 409 HANGER_TAKEN: otro puesto (u otra venta) ya tomó esa percha mientras
            // esta estaba en la cola. Reintentar es inútil: SIEMPRE va a fallar, y la
            // venta quedaría rebotando para siempre sin registrarse nunca. Se saca de
            // la cola y se le avisa al operador para que la rehaga con otra percha.
            if (r.status === 409) {
              const d = await r.json().catch(() => ({}));
              if (d.code === 'HANGER_TAKEN') {
                setHangerConflicts(prev => [...prev, {
                  n: sale.hanger_number, prenda: sale.note || '', total: Number(sale.total || 0), at: sale.created_at,
                }]);
                reportTpvError({ headers: hdrs, eventId: session.eventId, kind: 'hanger_taken',
                  message: 'venta rechazada: la percha ya estaba ocupada al sincronizar',
                  route: '/api/tpv/sales', context: { status: 409 } });
                continue;   // fuera de la cola
              }
            }
            remaining.push(sale);
          }
        } catch { remaining.push(sale); }
        finally { clearTimeout(timer); }
        // Si se cortó la red en medio de la cola, paramos: el resto queda para el
        // próximo intento en vez de fallar una por una.
        if (!navigator.onLine) {
          const idx = q.indexOf(sale);
          for (let k = idx + 1; k < q.length; k++) remaining.push(q[k]);
          break;
        }
      }
    } finally {
      syncingRef.current = false;
    }
    saveQueue(remaining);
    // Ventas que el servidor no aceptó: son cobros que todavía no están registrados.
    // Queda en la caja negra para poder revisarlo después de la noche.
    if (remaining.length) {
      reportTpvError({ headers: hdrs, eventId: session.eventId, kind: 'tpv_sync_fail',
        message: 'ventas sin sincronizar tras el reintento', route: '/api/tpv/sales',
        context: { size: remaining.length } });
    }
    refreshSold();
  }, [loadQueue, saveQueue, refreshSold, ensureToken]); // eslint-disable-line

  // Latido de la caja: 1 vez por minuto, fire-and-forget, fuera del camino del cobro.
  // Es lo que va a mostrar si una caja se quedó sin red o con ventas sin sincronizar
  // durante la noche, sin depender de que alguien lo cuente.
  useEffect(() => {
    const id = setInterval(() => {
      reportTpvHeartbeat({
        headers: authHeaders, eventId: session.eventId,
        metrics: {
          online: navigator.onLine,
          queue_pending: pendingRef.current,
          scans_total: soldCountRef.current,
        },
      });
    }, HEARTBEAT_MS);
    return () => clearInterval(id);
  }, []); // eslint-disable-line

  useEffect(() => {
    const q0 = loadQueue();
    setPending(q0.length);
    setQueuedTotal(q0.reduce((a, s) => a + Number(s.total || 0), 0));
    setRetPending(loadRetQueue().length);
    // Las devoluciones van DESPUÉS de las ventas: una devolución de una venta que
    // todavía no subió no tendría a qué aplicarse.
    const on = () => { setOnline(true); syncQueue(); setTimeout(syncReturns, 1200); };
    const off = () => setOnline(false);
    window.addEventListener('online', on); window.addEventListener('offline', off);
    if (navigator.onLine) { syncQueue(); setTimeout(syncReturns, 1200); }
    // REINTENTO PERIÓDICO. El evento 'online' del navegador solo se dispara si el
    // dispositivo pierde la interfaz de red, NO si el servidor se traba: en ese caso
    // el cobro se encolaba y se quedaba ahí hasta que alguien recargara la app —
    // plata cobrada sin llegar al servidor y sin que nadie se entere hasta el cierre.
    // Solo hace trabajo si hay algo pendiente, y el candado de syncQueue evita que se
    // solape con el sync del evento 'online'.
    const retry = setInterval(() => {
      if (!navigator.onLine) return;
      if (pendingRef.current > 0) syncQueue();
      if (retPendingRef.current > 0) syncReturns();
    }, 30000);
    return () => {
      clearInterval(retry);
      window.removeEventListener('online', on); window.removeEventListener('offline', off);
    };
  }, [syncQueue, loadQueue, syncReturns, loadRetQueue]);

  // Espejo del contador de devoluciones, para que el reintento periódico no dependa
  // del estado (que lo re-crearía en cada cambio).
  useEffect(() => { retPendingRef.current = retPending; }, [retPending]);

  useEffect(() => { pendingRef.current = pending; }, [pending]);
  useEffect(() => { soldCountRef.current = serverCount; }, [serverCount]);

  // ── Caja negra del TPV ──
  // Latido con lo que importa de una caja: si está online y cuántas ventas quedaron
  // sin sincronizar. No mide análisis (el TPV no procesa video).
  useEffect(() => {
    const id = setInterval(() => {
      reportTpvHeartbeat({ headers: authHeaders, eventId: session.eventId,
        metrics: { online: navigator.onLine, queue_pending: pendingRef.current, scans_total: soldCountRef.current } });
    }, HEARTBEAT_MS);
    return () => clearInterval(id);
  }, []); // eslint-disable-line

  // Errores de JS del TPV (el "se tildó" que no deja rastro).
  useEffect(() => {
    const onErr = (e) => reportTpvError({ headers: authHeaders, eventId: session.eventId, kind: 'js_error', message: e?.message || 'error', route: '/tpv' });
    const onRej = (e) => reportTpvError({ headers: authHeaders, eventId: session.eventId, kind: 'unhandled_rejection', message: e?.reason?.message || String(e?.reason || ''), route: '/tpv' });
    window.addEventListener('error', onErr);
    window.addEventListener('unhandledrejection', onRej);
    return () => { window.removeEventListener('error', onErr); window.removeEventListener('unhandledrejection', onRej); };
  }, []); // eslint-disable-line

  // app nativa: reconectar a la impresora guardada al abrir la caja
  useEffect(() => {
    if (NATIVE && nativeAddr) { connectNativePrinter(nativeAddr).catch(() => {}); }
  }, []); // eslint-disable-line

  // ticket helpers
  const add = (p) => setTicket(t => {
    const ex = t.find(i => i.id === p.id);
    if (ex) return t.map(i => i.id === p.id ? { ...i, qty: i.qty + 1 } : i);
    return [...t, { id: p.id, name: p.name, price: Number(p.price), qty: 1 }];
  });
  // Boletería + lote de cumpleaños → en vez de agregar directo, abre el selector de la lista.
  const tapProduct = (p) => { if (isBoleteria && p.is_birthday) { setBdPicker(p); setBdSearch(''); } else add(p); };
  const addBirthday = (guest, lot) => {
    setTicket(t => t.find(i => i.guestId === guest.id) ? t : [...t, { id: 'bd_' + guest.id, name: `🎂 ${guest.last_name}, ${guest.first_name}`, price: 0, qty: 1, guestId: guest.id, lotId: lot.id }]);
    setBdPicker(null); setBdSearch('');
  };
  const inc = (id) => setTicket(t => t.map(i => i.id === id ? { ...i, qty: i.qty + 1 } : i));
  const dec = (id) => setTicket(t => t.flatMap(i => i.id === id ? (i.qty > 1 ? [{ ...i, qty: i.qty - 1 }] : []) : [i]));
  const total = ticket.reduce((a, i) => a + i.price * i.qty, 0);
  // Saldo de mesa aplicado: paga hasta lo que quede del saldo, el resto va al cliente.
  const mesaDiscount = mesaApplied ? Math.min(Number(mesaApplied.remaining) || 0, total) : 0;
  const charged = Math.max(0, total - mesaDiscount);
  // RECARGO POR PAGAR CON QR. El % es del boliche y viaja en la sesión de la caja.
  // Se calcula sobre lo que el cliente paga de verdad: si una mesa cubrió parte, el
  // recargo va sobre el resto, no sobre el consumo entero.
  const recargoPct = Number(session.recargoQr) || 0;
  const baseCobro = (isBarra && mesaApplied) ? charged : total;
  const recargoQr = Math.round(baseCobro * recargoPct / 100);

  // Antes esto hacía JSON.parse de la cola COMPLETA en cada render (localStorage es
  // sincrónico: bloquea el hilo). Ahora sale de un estado que saveQueue mantiene.
  const vendido = serverSold + queuedTotal;

  // Aplica un saldo de mesa por código (busca en los saldos cacheados de esta caja).
  function applyMesa(code) {
    const c = (mesaCredits || []).find(x => String(x.code).toUpperCase() === String(code).trim().toUpperCase());
    if (!c) { alert('Ese código de mesa no corresponde a esta barra (o no existe).'); return; }
    if (Number(c.remaining) <= 0) { alert('Esa mesa ya no tiene saldo de consumo.'); return; }
    setMesaApplied({ code: c.code, remaining: Number(c.remaining), mesa_name: c.mesa_name });
    setMesaModal(false); setMesaCodeInput(''); setMesaReveal(false);
  }

  async function cobrar(medio) {
    // GUARD DE DOBLE COBRO. Con la red lenta el cobro tardaba sin mostrar nada, el
    // operador volvía a apretar y se disparaba una segunda venta idéntica. El ref
    // (no el estado) frena incluso dos toques en el mismo instante, porque un
    // setState no se ve reflejado hasta el próximo render.
    if (cobrandoRef.current) return;
    // Guardarropa: hacen falta LAS DOS cosas. Sin prenda se cobraba $0 igual (venta
    // vacía, ticket impreso y percha ocupada sin plata); sin percha no hay número que
    // reservar.
    if (isGuarda && (total <= 0 || !hangerPick)) return;
    if (isBoleteria ? ticket.length === 0 : (isGuarda ? total < 0 : total <= 0)) return; // boletería emite aunque sea gratis
    // Boletería: el QR lo emite el servidor → no funciona offline.
    if (isBoleteria && !navigator.onLine) { alert('La boletería necesita conexión para emitir el QR de la entrada.'); return; }
    cobrandoRef.current = true;
    setCobrando(true);
    try {
      await hacerCobro(medio === 'qr' ? 'qr' : 'efectivo');
    } finally {
      cobrandoRef.current = false;
      setCobrando(false);
    }
  }

  async function hacerCobro(medio) {
    // El recargo solo existe si se pagó con QR. Se manda aparte del total (que ya lo
    // incluye) para poder mostrarlo como línea en el ticket y separarlo del consumo.
    const recargo = medio === 'qr' ? recargoQr : 0;
    const localNumber = serverCount + loadQueue().length + 1;
    const sale = {
      // Id propio de ESTA venta: el servidor lo usa para no registrarla dos veces.
      // Se genera UNA sola vez acá, así el reintento (o el reenvío desde la cola)
      // manda el mismo id y el cobro es idempotente de punta a punta.
      client_sale_id: (crypto?.randomUUID?.() || ('s' + Date.now() + Math.random().toString(36).slice(2, 10))),
      box_id: session.boxId, event_id: session.eventId,
      items: isGuarda ? [{ name: note || 'Guardarropa', price: total, qty: 1 }]
        : isBoleteria ? ticket.map(i => ({ lot_id: i.lotId || i.id, name: i.name, price: i.price, qty: i.qty, birthday_guest_id: i.guestId || undefined }))
        : ticket.map(i => ({ product_id: i.id, name: i.name, price: i.price, qty: i.qty })),
      // barra con saldo de mesa: se cobra lo que el saldo NO cubre; el resto lo paga la mesa.
      total: baseCobro + recargo,
      payment_method: medio,
      surcharge: recargo,
      note: isGuarda ? (note || null) : null,
      // La percha elegida en la grilla queda RESERVADA por la venta. El número ya no
      // depende de la impresión: si falla el papel, se reimprime el mismo número.
      ...(isGuarda && hangerPick ? { hanger_number: hangerPick.n, hanger_extra: !!hangerPick.extra } : {}),
      created_at: new Date().toISOString(),
      ...(isBarra && mesaApplied && mesaDiscount > 0 ? { mesa_code: mesaApplied.code, mesa_paid: mesaDiscount } : {}),
    };
    let saved = null;
    if (navigator.onLine) {
      // TIMEOUT DURO. Antes este await no tenía tope: si la red colgaba sin
      // responder, el cobro no terminaba nunca (no se limpiaba el ticket, no se
      // imprimía, la pantalla no mostraba nada) — el "se tildó" del viernes 24.
      // Al cortar, la venta cae a la cola offline; el client_sale_id garantiza que
      // si el servidor SÍ la había registrado, el reenvío no la duplique.
      const ctrl = new AbortController();
      const timer = setTimeout(() => ctrl.abort(), SALE_TIMEOUT_MS);
      try {
        const r = await fetch(`${API}/api/tpv/sales`, { method: 'POST', headers: authHeaders, body: JSON.stringify(sale), signal: ctrl.signal });
        if (r.ok) saved = await r.json();
        else if (r.status === 409) {
          const d = await r.json().catch(() => ({}));
          // La caja fue eliminada del panel: encolar sería seguir cobrando contra algo
          // que no existe. Se frena acá y se avisa, en vez de acumular ventas perdidas.
          if (d.code === 'BOX_GONE') {
            alert(d.error || 'Esta caja ya no existe. Cerrá la caja y volvé a abrirla.');
            return;
          }
        }
      } catch {
        reportTpvError({ headers: authHeaders, eventId: session.eventId, kind: 'sale_timeout',
          message: 'sin respuesta del servidor al cobrar; la venta quedo en la cola offline',
          route: '/api/tpv/sales', context: { duration_ms: SALE_TIMEOUT_MS, online: true } });
      } finally { clearTimeout(timer); }
    }
    if (isBoleteria) {
      if (!saved) { alert('No se pudo emitir la entrada. Revisá la conexión e intentá de nuevo.'); return; }
      const target = (NATIVE && nativeAddr) ? { nativeAddr } : (printer?.characteristic ? { characteristic: printer.characteristic } : {});
      // Timeout proporcional a la cantidad de entradas (se imprimen una tras otra),
      // con un piso. Sin esto, una impresora trabada dejaba la caja sin poder seguir
      // cobrando — y en boletería se emiten tandas de varias entradas por venta.
      // Las consumiciones salen aparte: son vales sin QR (no son entradas), y una venta
      // puede traer las dos cosas (la entrada del RRPP y su consumición).
      const entradas = saved.qr_codes || [];
      const vales = saved.vales || [];
      const n = Math.max(1, entradas.length + vales.length);
      try {
        await conTimeout((async () => {
          if (entradas.length) await printBoleteriaTickets(entradas, session, target);
          if (vales.length) await printVales(vales, session, target);
        })(), PRINT_TIMEOUT_MS + n * 4000);
      }
      catch (e) {
        reportTpvError({ headers: authHeaders, eventId: session.eventId, kind: 'print_fail',
          message: 'no se pudieron imprimir las entradas de boleteria: ' + (e?.message || e), route: '/tpv',
          context: { size: n, reason: e?.message === 'TIMEOUT' ? 'timeout' : 'error' } });
        // La entrada YA existe en el sistema: el admin la reimprime desde el panel.
        alert(vales.length && !entradas.length
          ? 'La consumición quedó cobrada, pero no se pudo imprimir el vale. Cobrala de nuevo o entregala a mano.'
          : `La entrada quedó emitida y cobrada, pero no se pudo imprimir.\nPedile al administrador que la reimprima desde el panel (Cajas → Ver resumen).`);
      }
      setTicket([]); setNote(''); refreshSold(); loadBirthdayGuests();
      return;
    }
    if (!saved) {
      const q = loadQueue(); q.push(sale); saveQueue(q);
      saved = { ...sale, ticket_number: localNumber, offline: true };
    }
    // saldo de mesa: descontar del cache local (esta caja es la única que lo toca → offline OK)
    if (isBarra && mesaApplied && mesaDiscount > 0) {
      setMesaCredits(cs => {
        const upd = cs.map(c => c.code === mesaApplied.code ? { ...c, remaining: Math.max(0, Number(c.remaining) - mesaDiscount) } : c);
        try { localStorage.setItem(mesaCacheKey, JSON.stringify(upd)); } catch {}
        return upd;
      });
    }
    setMesaApplied(null);

    if (isGuarda) {
      // GUARDARROPA: cobrar NO imprime. La percha ya quedó reservada por la venta y
      // la impresión pasa a ser una acción aparte y repetible (dos tickets: cliente y
      // percha). Así quedarse sin papel no genera números fantasma.
      setPrintModal({ sale: saved });
      setHangerPick(null);
      setTicket([]); setNote('');
      // La percha se marca ocupada al instante, sin esperar al servidor: si la red
      // está caída, la grilla igual tiene que mostrarla tomada.
      setHangers(h => {
        const ya = (h.ocupadas || []).some(o => Number(o.n) === Number(saved.hanger_number));
        if (ya || saved.hanger_number == null) return h;
        const upd = { ...h, ocupadas: [...(h.ocupadas || []), { n: saved.hanger_number, prenda: saved.note || '', total: saved.total, at: saved.created_at, extra: !!saved.hanger_extra, mine: true }] };
        try { localStorage.setItem(hangerCacheKey, JSON.stringify(upd)); } catch {}
        return upd;
      });
      refreshSold();
      return;
    }

    // imprimir
    printTicket(saved);
    setTicket([]); setNote('');
    refreshSold();
  }

  async function linkPrinter() {
    if (!bluetoothAvailable()) {
      alert('Este navegador no soporta Web Bluetooth. Usá "Probar impresión": el ticket sale por el diálogo de impresión del sistema, donde elegís tu impresora (USB, WiFi o Bluetooth agregada en Android) o PDF.');
      return;
    }
    try {
      const conn = await connectBluetoothPrinter();
      setPrinter(conn);
      alert('Impresora vinculada: ' + (conn.device.name || 'Bluetooth'));
    } catch (e) {
      alert('No se pudo vincular por Bluetooth. Web Bluetooth solo funciona con impresoras BLE; las Bluetooth "clásicas" no aparecen acá.\n\nLo más simple: imprimí con "Probar impresión" / al cobrar, y elegí tu impresora en el diálogo del sistema (USB, WiFi o Bluetooth configurada en la tablet).');
    }
  }

  // App nativa: elegir impresora Bluetooth clásica entre las emparejadas en Android.
  async function openPrinterPicker() {
    setPicker({ loading: true, devices: [] });
    try {
      const devices = await listNativePrinters();
      setPicker({ loading: false, devices });
    } catch (e) {
      setPicker(null);
      const code = e?.code || '';
      if (code === 'BT_OFF') alert('El Bluetooth está apagado. Activalo y reintentá.');
      else if (code === 'PERM_DENIED') alert('Hace falta el permiso de Bluetooth para listar impresoras.');
      else alert('No se pudieron listar las impresoras emparejadas.\nEmparejá la impresora desde Ajustes de Android y reintentá.');
    }
  }

  async function chooseNativePrinter(d) {
    try {
      await connectNativePrinter(d.address);
      setNativeAddr(d.address); setNativeName(d.name || '');
      try { localStorage.setItem(printerKey, d.address); localStorage.setItem(printerKey + '_name', d.name || ''); } catch {}
      setPicker(null);
    } catch (e) {
      // El motivo REAL (qué falló por cable serie y qué por BLE) lo da el plugin. Antes
      // se tapaba con "¿está encendida?" y desde la caja no había forma de saber si era
      // la impresora, el emparejamiento o el protocolo.
      const motivo = e?.message || e?.errorMessage || '';
      alert('No se pudo conectar con ' + (d.name || d.address) + '.\n¿Está encendida y en rango?'
        + (motivo ? '\n\nDetalle: ' + motivo : ''));
    }
  }

  async function testPrint() {
    const demo = { items: [{ name: 'PRUEBA', price: 0, qty: 1 }], total: 0, ticket_number: 0, note: 'PRUEBA', created_at: new Date().toISOString() };
    await printTicket(demo);
  }

  // variant: solo guardarropa. 'cliente' (comprobante para retirar) o 'percha'
  // (queda colgado con la prenda). Devuelve true si imprimio, false si fallo: el
  // modal necesita saberlo para no marcar como impreso algo que no salio.
  async function printTicket(sale, variant = 'cliente') {
    const logoUrl = session.ticket?.logo || null; // logo propio del menú (negro sobre transparente)
    // QR de ARCA del comprobante, si la venta se facturó a tiempo. Va impreso al pie:
    // los datos del comprobante van igual en el texto, el QR no los reemplaza.
    let fiscalQr = null;
    if (sale?.comprobante?.qr_url) {
      try {
        fiscalQr = await QRCode.toDataURL(sale.comprobante.qr_url, {
          margin: 1, width: 300, errorCorrectionLevel: 'M',
          color: { dark: '#000000ff', light: '#00000000' },
        });
      } catch { /* sin QR, el ticket sale con los datos igual */ }
    }
    // 1) App nativa con impresora SPP seleccionada → impresión directa (estilo Loyverse).
    if (NATIVE && nativeAddr) {
      try {
        // El 4º parámetro registra la caída del vínculo aunque el reintento la salve:
        // un Bluetooth que se corta seguido tiene que verse en la caja negra igual.
        await conTimeout(printNativeSPP(ticketLines(sale, session, variant), logoUrl, nativeAddr,
          (err) => reportTpvError({ headers: authHeaders, eventId: session.eventId, kind: 'print_retry',
            message: 'se cayo el vinculo con la impresora y se reimprimio tras reconectar: ' + (err?.message || err),
            route: '/tpv', context: { recuperado: true } }),
          fiscalQr
        ), PRINT_TIMEOUT_MS);
        return true;
      }
      catch (e) {
        // La impresora Bluetooth puede quedarse esperando para siempre (apagada, fuera
        // de rango, o el vínculo caído). El timeout devuelve la caja al operador.
        reportTpvError({ headers: authHeaders, eventId: session.eventId, kind: 'print_fail',
          message: 'no se pudo imprimir por Bluetooth: ' + (e?.message || e), route: '/tpv',
          context: { reason: e?.message === 'TIMEOUT' ? 'timeout' : 'error' } });
        alert('No se pudo imprimir en la impresora Bluetooth. Revisá que esté encendida.\nLa venta YA quedó cobrada.');
        return;
      }
    }
    // 2) Navegador con impresora BLE vinculada.
    if (printer?.characteristic) {
      try { await conTimeout(printBluetooth(printer.characteristic, ticketLines(sale, session, variant), logoUrl, fiscalQr), PRINT_TIMEOUT_MS); return true; }
      catch { /* cae a diálogo del sistema */ }
    }
    // 3) Fallback: diálogo de impresión del sistema.
    printBrowser(sale, session, variant);
    return true;
  }

  // agrupar productos por categoría
  const byCat = {};
  products.filter(p => p.active !== false).forEach(p => { (byCat[p.category || 'General'] = byCat[p.category || 'General'] || []).push(p); });

  return (
    // height (no minHeight): la caja ocupa EXACTAMENTE la pantalla y cada columna
    // scrollea por dentro. Con minHeight, la grilla de perchas hacía crecer la página
    // y arrastraba el botón de cobrar fuera de la vista — inaceptable con gente
    // esperando. dvh respeta la barra del navegador en la tablet.
    <div style={{ height: '100dvh', maxHeight: '100dvh', overflow: 'hidden', background: 'var(--bg)', display: 'flex', flexDirection: 'column' }}>
      {/* header */}
      <div style={{ borderBottom: '1px solid var(--border)', background: 'var(--ink-100)', padding: '12px 18px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
          <img src="/assets/tukipass-icon.png" alt="" style={{ height: 28 }} />
          <div style={{ minWidth: 0 }}>
            <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 14, color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {session.boxName}{isGuarda ? ' · 🧥' : isBoleteria ? ' · 🎟️' : ''}
            </div>
            <div style={{ fontSize: 11, color: 'var(--ink-600)' }}>{session.orgName} · {session.eventName} · vendido <b style={{ color: '#fff' }}>{money(vendido)}</b></div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ fontSize: 10, letterSpacing: '0.1em', textTransform: 'uppercase', color: online ? '#2ecc71' : 'var(--red-400)' }}>
            ● {online ? 'En línea' : 'Sin conexión'}{pending ? ` · ${pending}` : ''}{session.offlineOpen ? ' · abierta sin internet' : ''}
            {/* devoluciones sin subir: la prenda ya se entregó, falta avisarle al servidor */}
            {retPending ? <span style={{ color: '#d4af37' }}> · {retPending} dev.</span> : null}
          </span>
          {NATIVE ? (
            <>
              <button className="lb-btn lb-btn--ghost lb-btn--sm" onClick={openPrinterPicker} title="Elegir la impresora térmica Bluetooth">{nativeAddr ? `🖨 ${nativeName || 'Impresora'} ✓` : '🖨 Impresora'}</button>
              <button className="lb-btn lb-btn--ghost lb-btn--sm" onClick={testPrint} title="Imprimir un ticket de prueba">Probar</button>
            </>
          ) : (
            <>
              <button className="lb-btn lb-btn--ghost lb-btn--sm" onClick={() => printBrowser({ items: [{ name: 'PRUEBA', price: 0, qty: 1 }], total: 0, created_at: new Date().toISOString() }, session)} title="Imprimir un ticket de prueba con el diálogo del sistema">Probar impresión</button>
              <button className="lb-btn lb-btn--ghost lb-btn--sm" onClick={linkPrinter}>{printer ? 'Bluetooth ✓' : 'Bluetooth (BLE)'}</button>
            </>
          )}
          <button className="lb-btn lb-btn--ghost lb-btn--sm" onClick={() => setCalcOpen(true)} title="Sumar varios tickets">🧮 Calculadora</button>
          <button className="lb-btn lb-btn--ghost lb-btn--sm" onClick={() => { if (window.confirm('¿Cerrar la caja?')) onClose(); }}>Cerrar caja</button>
        </div>
      </div>

      {/* layout 2 columnas */}
      <div style={{ flex: 1, display: 'grid', gridTemplateColumns: '1.6fr 1fr', gap: 0, minHeight: 0 }}>
        {/* productos */}
        <div style={{ padding: 18, overflowY: 'auto' }}>
          {isGuarda ? (
            <>
              {/* Paso 1: la prenda (si el menú tiene precios cargados). Paso 2: la percha. */}
              {products.filter(p => p.active !== false).length > 0 && (
                <div style={{ marginBottom: 18 }}>
                  <div className="lb-eyebrow" style={{ marginBottom: 8 }}>1 · Prenda</div>
                  <GuardarropaPad products={products} add={add} />
                </div>
              )}
              <div className="lb-eyebrow" style={{ marginBottom: 8 }}>
                {products.filter(p => p.active !== false).length > 0 ? '2 · Percha' : 'Percha'}
              </div>
              {hangerPick && (
                <div style={{ marginBottom: 10, padding: '8px 12px', borderRadius: 6, background: 'rgba(46,204,113,0.12)', border: '1px solid rgba(46,204,113,0.45)', fontSize: 13, color: '#fff', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  Percha <b style={{ fontSize: 17 }}>{hangerPick.n}</b> elegida{hangerPick.extra ? ' (extra)' : ''} · cobrá para reservarla
                  <button className="lb-btn lb-btn--ghost lb-btn--sm" onClick={() => setHangerPick(null)}>Cambiar</button>
                </div>
              )}
              <HangerGrid
                from={hangers.from} to={hangers.to} ocupadas={hangers.ocupadas}
                pendingLocal={hangersPendientes()}
                onPick={(n, extra) => setHangerPick({ n, extra })}
                onOccupied={(n, oc) => setHangerBusy({ n, oc })}
              />
            </>
          ) : (() => {
            const catNames = Object.keys(byCat);
            if (catNames.length === 0) return (
              <div style={{ color: 'var(--ink-600)', fontSize: 13, padding: 40, textAlign: 'center' }}>
                {isBoleteria
                  ? <>
                      <div style={{ marginBottom: 12, maxWidth: 460, marginLeft: 'auto', marginRight: 'auto' }}>{bolDiag || 'Esta caja no tiene lotes de boletería para vender. Revisá que el evento tenga lotes con canal “Boletería” (aplicá el grupo en Productos).'}</div>
                      <button className="lb-btn lb-btn--ghost lb-btn--sm" onClick={loadBoleteriaLots}>Reintentar</button>
                    </>
                  : 'Este menú no tiene productos cargados.'}
              </div>
            );
            const activeCat = cat && catNames.includes(cat) ? cat : catNames[0];
            return (
              <>
                {/* categorías horizontales (tabs) */}
                <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 12, marginBottom: 16, borderBottom: '1px solid var(--border)', position: 'sticky', top: 0, background: 'var(--bg)', zIndex: 2 }}>
                  {catNames.map(c => (
                    <button key={c} onClick={() => setCat(c)} style={{
                      flex: 'none', padding: '8px 16px', borderRadius: 999, cursor: 'pointer', fontSize: 13, fontWeight: 700, whiteSpace: 'nowrap',
                      fontFamily: 'var(--font-display)', letterSpacing: '0.06em', textTransform: 'uppercase',
                      border: '1px solid ' + (c === activeCat ? 'var(--red-500)' : 'var(--border)'),
                      background: c === activeCat ? 'var(--red-600, #C8102E)' : 'var(--ink-200)',
                      color: '#fff',
                    }}>{c} <span style={{ opacity: 0.6, fontSize: 11 }}>{byCat[c].length}</span></button>
                  ))}
                </div>
                {/* productos de la categoría activa */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: 10 }}>
                  {byCat[activeCat].map(p => (
                    <button key={p.id} onClick={() => tapProduct(p)} style={{ background: 'var(--ink-200)', border: '1px solid var(--border)', borderRadius: 4, padding: '16px 12px', cursor: 'pointer', textAlign: 'left', color: '#fff', transition: 'border-color 160ms' }}
                      onMouseDown={e => e.currentTarget.style.borderColor = 'var(--red-500)'} onMouseUp={e => e.currentTarget.style.borderColor = 'var(--border)'}>
                      <div style={{ fontWeight: 600, fontSize: 14, lineHeight: 1.2, marginBottom: 8 }}>{p.name}</div>
                      <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, color: 'var(--ink-800)' }}>{money(p.price)}</div>
                    </button>
                  ))}
                </div>
              </>
            );
          })()}
        </div>

        {/* ticket */}
        <div style={{ borderLeft: '1px solid var(--border)', background: 'var(--ink-100)', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <div style={{ padding: '16px 18px', borderBottom: '1px solid var(--border)' }}>
            <div className="lb-eyebrow">Ticket actual</div>
          </div>
          <div style={{ flex: 1, overflowY: 'auto', padding: '12px 18px' }}>
            {!isGuarda && ticket.length === 0 && <div style={{ color: 'var(--ink-600)', fontSize: 13, textAlign: 'center', padding: '40px 0' }}>Tocá productos para agregarlos</div>}
            {!isGuarda && ticket.map(i => (
              <div key={i.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '10px 0', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ color: '#fff', fontSize: 14, fontWeight: 600 }}>{i.name}</div>
                  <div style={{ fontSize: 12, color: 'var(--ink-600)' }}>{money(i.price)} c/u</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <button className="lb-iconbtn" onClick={() => dec(i.id)}>–</button>
                  <span style={{ minWidth: 22, textAlign: 'center', fontFamily: 'var(--font-display)', fontWeight: 700 }}>{i.qty}</span>
                  <button className="lb-iconbtn" onClick={() => inc(i.id)}>+</button>
                  <span style={{ minWidth: 70, textAlign: 'right', fontWeight: 600, color: '#fff' }}>{money(i.price * i.qty)}</span>
                </div>
              </div>
            ))}
            {isGuarda && (() => {
              // Control interno: si el dueño cargó prendas con precio, el cajero SOLO cobra
              // el precio fijo del producto seleccionado (no editable) → no puede cobrar un
              // valor y registrar otro. Solo si NO hay productos cargados (sin precio de
              // referencia) se mantiene la carga manual del precio. La descripción es libre.
              const hasProds = products.filter(p => p.active !== false).length > 0;
              return (
              <div style={{ paddingTop: 8 }}>
                <div className="lb-label" style={{ marginBottom: 8 }}>Descripción de la prenda (opcional)</div>
                <input className="lb-input" value={note} onChange={e => setNote(e.target.value)} placeholder="Ej: campera negra" />
                <div style={{ marginTop: 16 }} className="lb-label">Precio</div>
                {hasProds ? (
                  <div style={{ marginTop: 8, fontSize: 22, fontFamily: 'var(--font-display)', fontWeight: 700, color: ticket[0] ? '#fff' : 'var(--ink-600)' }}>
                    {ticket[0] ? money(ticket[0].price) : 'Elegí una prenda ←'}
                  </div>
                ) : (
                  <div style={{ marginTop: 8 }}>
                    <input className="lb-input" inputMode="numeric" value={ticket[0]?.price || ''} placeholder="0"
                      onChange={e => { const v = +e.target.value.replace(/\D/g, '') || 0; setTicket(v ? [{ id: 'guarda', name: 'Guardarropa', price: v, qty: 1 }] : []); }}
                      style={{ fontSize: 20 }} />
                  </div>
                )}
              </div>
              );
            })()}
          </div>
          <div style={{ padding: '16px 18px', borderTop: '1px solid var(--border)' }}>
            {isBarra && mesaCredits.length > 0 && !mesaApplied && (
              <button className="lb-btn lb-btn--ghost lb-btn--block lb-btn--sm" style={{ marginBottom: 12 }} onClick={() => setMesaModal(true)}>🍷 Aplicar saldo de mesa</button>
            )}
            {isBarra && mesaApplied && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10, fontSize: 12.5 }}>
                <span style={{ color: 'var(--ink-700)' }}>Mesa {mesaApplied.mesa_name || ''} · <b style={{ color: '#fff' }}>−{money(mesaDiscount)}</b> <span style={{ color: 'var(--ink-600)' }}>(queda {money(Math.max(0, Number(mesaApplied.remaining) - mesaDiscount))})</span></span>
                <button onClick={() => setMesaApplied(null)} style={{ background: 'transparent', border: 0, color: 'var(--red-400)', cursor: 'pointer', fontSize: 12 }}>Quitar</button>
              </div>
            )}
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 14 }}>
              <span className="lb-eyebrow">{isBarra && mesaApplied ? 'A cobrar' : 'Total'}</span>
              <span style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 34, color: '#fff' }}>{money(isBarra && mesaApplied ? charged : total)}</span>
            </div>
            {/* El botón se bloquea mientras cobra y lo dice: antes no mostraba nada,
                el operador creía que no había pasado y volvía a apretar. */}
            {/* En guardarropa el botón dice QUÉ falta (prenda o percha), en vez de
                quedarse apagado sin explicar por qué. */}
            {(() => {
              const falta = isGuarda ? (total <= 0 ? 'Elegí la prenda' : !hangerPick ? 'Elegí la percha' : null)
                : isBoleteria ? (ticket.length === 0 ? 'Cargá la entrada' : null)
                : (total <= 0 ? 'Cargá el pedido' : null);
              if (falta || cobrando) {
                return (
                  <button className="lb-btn lb-btn--primary lb-btn--block" style={{ padding: '20px', fontSize: 18, opacity: 0.75 }} disabled>
                    {cobrando ? (isBoleteria ? 'Emitiendo…' : 'Cobrando…') : falta}
                  </button>
                );
              }
              // El medio de pago se elige acá y no en un paso aparte: en la barra cada
              // toque de más es cola. Lo que cambia entre los dos botones es el importe,
              // así el cajero ve el recargo antes de cobrar y se lo puede decir al cliente.
              return (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <button className="lb-btn lb-btn--primary lb-btn--block" style={{ padding: '18px 10px', fontSize: 16 }} onClick={() => cobrar('efectivo')}>
                    EFECTIVO
                    <div style={{ fontSize: 13, fontWeight: 500, opacity: 0.85, marginTop: 2 }}>{money(baseCobro)}</div>
                  </button>
                  <button className="lb-btn lb-btn--white lb-btn--block" style={{ padding: '18px 10px', fontSize: 16 }} onClick={() => cobrar('qr')}>
                    QR{recargoPct > 0 ? ` +${recargoPct}%` : ''}
                    <div style={{ fontSize: 13, fontWeight: 500, opacity: 0.85, marginTop: 2 }}>{money(baseCobro + recargoQr)}</div>
                  </button>
                </div>
              );
            })()}
          </div>
        </div>
      </div>

      {/* Selector de impresora Bluetooth clásica (app nativa) */}
      {picker && (
        <div onClick={() => setPicker(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50, padding: 20 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: 'var(--ink-100)', border: '1px solid var(--border)', borderRadius: 4, width: '100%', maxWidth: 420, padding: 22 }}>
            <div className="lb-eyebrow" style={{ marginBottom: 4 }}>Impresora térmica</div>
            <div style={{ fontSize: 12, color: 'var(--ink-600)', marginBottom: 16 }}>Elegí una impresora ya emparejada en Android. Si no aparece, emparejala primero desde Ajustes &gt; Bluetooth.</div>
            {picker.loading ? (
              <div style={{ color: 'var(--ink-600)', fontSize: 13, padding: '20px 0', textAlign: 'center' }}>Buscando impresoras…</div>
            ) : picker.devices.length === 0 ? (
              <div style={{ color: 'var(--ink-600)', fontSize: 13, padding: '20px 0', textAlign: 'center' }}>No hay dispositivos emparejados.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {picker.devices.map(d => {
                  // El TPV habla los dos protocolos: Bluetooth clásico (SPP) y BLE. El
                  // tipo se muestra igual porque es lo primero que hay que mirar cuando
                  // una impresora no conecta.
                  const via = d.type === 'le' ? 'BLE' : d.type === 'classic' ? 'clásico' : d.type === 'dual' ? 'clásico o BLE' : null;
                  return (
                    <button key={d.address} onClick={() => chooseNativePrinter(d)} style={{ background: 'var(--ink-200)', border: '1px solid ' + (d.address === nativeAddr ? 'var(--red-500)' : 'var(--border)'), borderRadius: 4, padding: '12px 14px', cursor: 'pointer', textAlign: 'left', color: '#fff' }}>
                      <div style={{ fontWeight: 600, fontSize: 14 }}>{d.name || 'Sin nombre'}{d.address === nativeAddr ? ' ✓' : ''}</div>
                      {/* "sin SPP" es el dato que explica la mitad de los fallos: un
                          aparato clásico que NO anuncia el perfil serie no va a conectar
                          por ahí, y hay que dejarlo ir por BLE. */}
                      <div style={{ fontSize: 11, color: 'var(--ink-600)' }}>
                        {d.address}{via ? ` · ${via}` : ''}{d.spp === false && d.type !== 'le' ? ' · sin SPP' : ''}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
            <button className="lb-btn lb-btn--ghost lb-btn--sm" style={{ marginTop: 16 }} onClick={() => setPicker(null)}>Cerrar</button>
          </div>
        </div>
      )}

      {/* Calculadora para sumar varios tickets en la barra */}
      {calcOpen && <CalcModal onClose={() => setCalcOpen(false)} />}

      {/* GUARDARROPA · después de cobrar: los DOS tickets, cada uno por separado y
          repetible. Cobrar y imprimir son acciones distintas: si falla el papel se
          reintenta acá sin generar otro número. */}
      {/* Ventas que el servidor rechazó porque la percha ya estaba ocupada. NO se
          pueden reintentar (siempre fallarían), así que se muestran hasta que el
          operador las vea: es plata cobrada que no quedó registrada. */}
      {hangerConflicts.length > 0 && (
        <div className="lb-modal-backdrop" style={{ zIndex: 400 }}>
          <div className="lb-modal" style={{ maxWidth: 460, padding: 26 }}>
            <div style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 20, color: 'var(--red-400)', textTransform: 'uppercase' }}>
              {hangerConflicts.length === 1 ? 'Una venta no se registró' : `${hangerConflicts.length} ventas no se registraron`}
            </div>
            <div style={{ fontSize: 13, color: 'var(--ink-700)', marginTop: 10, lineHeight: 1.5 }}>
              La percha ya estaba ocupada cuando se sincronizó (otro puesto la tomó primero).
              <b style={{ color: '#fff' }}> Estas ventas hay que volver a cobrarlas con otra percha.</b>
            </div>
            <div style={{ margin: '16px 0', maxHeight: 220, overflowY: 'auto' }}>
              {hangerConflicts.map((c, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, padding: '10px 0', borderTop: '1px solid var(--border)' }}>
                  <div>
                    <div style={{ color: '#fff', fontWeight: 700 }}>Percha {c.n}</div>
                    <div style={{ fontSize: 12, color: 'var(--ink-600)' }}>{c.prenda || 'Sin descripción'}</div>
                  </div>
                  <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, color: '#fff' }}>{money(c.total)}</div>
                </div>
              ))}
            </div>
            <button className="lb-btn lb-btn--primary lb-btn--block" onClick={() => setHangerConflicts([])}>Entendido</button>
          </div>
        </div>
      )}

      {printModal && (
        <PrintTicketsModal
          sale={printModal.sale}
          onPrint={(variant) => printTicket(printModal.sale, variant)}
          onClose={() => { setPrintModal(null); loadHangers(); }}
        />
      )}

      {/* GUARDARROPA · percha ocupada: devolver, o reimprimir cualquiera de los dos. */}
      {hangerBusy && (
        <HangerBusyModal
          n={hangerBusy.n}
          oc={hangerBusy.oc}
          onPrint={(variant) => printTicket({
            hanger_number: hangerBusy.n, note: hangerBusy.oc?.prenda || '',
            total: hangerBusy.oc?.total || 0, created_at: hangerBusy.oc?.at || new Date().toISOString(),
            items: [{ name: hangerBusy.oc?.prenda || 'Guardarropa', price: hangerBusy.oc?.total || 0, qty: 1 }],
          }, variant)}
          onReturn={() => devolverPercha(hangerBusy.n)}
          onClose={() => setHangerBusy(null)}
        />
      )}

      {/* Saldo de mesa (barra): el CLIENTE ingresa su código (el cajero no lo ve ni lo conoce) */}
      {mesaModal && (
        <div className="lb-modal-backdrop" onClick={() => setMesaModal(false)}>
          <div className="lb-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 440 }}>
            <div style={{ padding: '20px 24px', borderBottom: '1px solid rgba(255,255,255,0.08)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div className="lb-eyebrow">Saldo de mesa</div>
                <div style={{ fontSize: 12, color: 'var(--ink-600)', marginTop: 2 }}>Que el cliente ingrese su código</div>
              </div>
              <button className="lb-iconbtn" onClick={() => setMesaModal(false)}>✕</button>
            </div>
            <div style={{ padding: '18px 24px' }}>
              <div style={{ fontSize: 13, color: 'var(--ink-700)', marginBottom: 12, lineHeight: 1.5 }}>Pasale la tablet al cliente: el código está en su link de entradas. Es privado, no lo compartas con nadie más.</div>
              <input className="lb-input" autoFocus placeholder="Código del cliente" value={mesaMasked} onChange={onMesaCode} onKeyDown={e => e.key === 'Enter' && applyMesa(mesaCodeInput)} autoCapitalize="characters" autoComplete="off" spellCheck={false} style={{ textAlign: 'center', fontSize: 22, letterSpacing: '0.3em', fontFamily: 'monospace' }} />
              <button className="lb-btn lb-btn--primary lb-btn--block" style={{ marginTop: 12 }} onClick={() => applyMesa(mesaCodeInput)}>Aplicar saldo</button>
            </div>
          </div>
        </div>
      )}

      {/* Selector de cumpleañeros (boletería): elegir de la lista para emitir su entrada */}
      {bdPicker && (
        <div className="lb-modal-backdrop" onClick={() => setBdPicker(null)}>
          <div className="lb-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 480 }}>
            <div style={{ padding: '20px 24px', borderBottom: '1px solid rgba(255,255,255,0.08)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div className="lb-eyebrow">Cumpleañeros</div>
                <div style={{ fontSize: 12, color: 'var(--ink-600)', marginTop: 2 }}>Elegí quién ingresa · se emite su entrada</div>
              </div>
              <button className="lb-iconbtn" onClick={() => setBdPicker(null)}>✕</button>
            </div>
            <div style={{ padding: '16px 24px' }}>
              <input className="lb-input" autoFocus placeholder="Buscar por apellido o DNI…" value={bdSearch} onChange={e => setBdSearch(e.target.value)} style={{ marginBottom: 12 }} />
              <div style={{ maxHeight: '50vh', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 6 }}>
                {(() => {
                  const q = bdSearch.trim().toLowerCase();
                  const inTicket = new Set(ticket.map(i => i.guestId).filter(Boolean));
                  // Se busca por apellido O por DNI: el cajero tiene el documento en la
                  // mano, y tipear el número es más rápido y más seguro que un apellido
                  // que puede estar escrito distinto.
                  const soloNum = q.replace(/\D/g, '');
                  const list = birthdayGuests.filter(g => !inTicket.has(g.id) && (!q
                    || `${g.last_name} ${g.first_name}`.toLowerCase().includes(q)
                    || (soloNum && String(g.dni || '').includes(soloNum))));
                  if (birthdayGuests.length === 0) return <div style={{ fontSize: 13, color: 'var(--ink-600)', padding: '10px 0' }}>No hay cumpleañeros pendientes. Los RRPP los cargan desde su panel.</div>;
                  if (list.length === 0) return <div style={{ fontSize: 13, color: 'var(--ink-600)', padding: '10px 0' }}>Nadie coincide con la búsqueda.</div>;
                  return list.map(g => (
                    <button key={g.id} onClick={() => addBirthday(g, bdPicker)} style={{ textAlign: 'left', background: 'var(--ink-100)', border: '1px solid var(--border)', borderRadius: 4, padding: '12px 14px', color: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>
                      {g.last_name}, <span style={{ fontWeight: 400, color: 'var(--ink-800)' }}>{g.first_name}</span>
                      {/* El DNI y la fecha los traen los que se anotaron solos por la web.
                          Es contra esto que el cajero compara el documento físico: antes
                          tenía solo un nombre y decidía a ojo. Los que cargó un RRPP no los
                          tienen, y ahí no se muestra nada. */}
                      {(g.dni || g.birthdate) && (
                        <div style={{ fontSize: 12, fontWeight: 400, color: 'var(--ink-700)', marginTop: 3 }}>
                          {g.dni ? `DNI ${g.dni}` : ''}{g.dni && g.birthdate ? ' · ' : ''}{g.birthdate ? `nació el ${String(g.birthdate).slice(0,10).split('-').reverse().join('/')}` : ''}
                        </div>
                      )}
                    </button>
                  ));
                })()}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* Calculadora táctil simple para que el cajero sume tickets sueltos. */
function CalcModal({ onClose }) {
  const [expr, setExpr] = useState('');   // expresión visible, ej "1500+2300"
  const [result, setResult] = useState(''); // resultado calculado

  const evalExpr = (s) => {
    // Solo dígitos, punto y operadores: evaluación segura sin eval().
    const clean = s.replace(/[^0-9.+\-*/]/g, '');
    if (!clean) return '';
    try {
      // eslint-disable-next-line no-new-func
      const v = Function('"use strict";return (' + clean + ')')();
      if (typeof v !== 'number' || !isFinite(v)) return '';
      return String(Math.round(v * 100) / 100);
    } catch { return ''; }
  };

  const press = (k) => {
    if (k === 'C') { setExpr(''); setResult(''); return; }
    if (k === '⌫') { setExpr(e => e.slice(0, -1)); return; }
    if (k === '=') { const r = evalExpr(expr); if (r !== '') { setExpr(r); setResult(''); } return; }
    setExpr(e => {
      const ops = '+-*/';
      // evitar dos operadores seguidos
      if (ops.includes(k) && (e === '' || ops.includes(e.slice(-1)))) {
        return ops.includes(e.slice(-1)) ? e.slice(0, -1) + k : e;
      }
      return e + k;
    });
  };

  // mostrar resultado en vivo del lado derecho
  const live = result || (expr ? evalExpr(expr) : '');

  const keys = ['C', '⌫', '/', '*', '7', '8', '9', '-', '4', '5', '6', '+', '1', '2', '3', '=', '0', '00', '.'];
  const isOp = (k) => '+-*/'.includes(k);

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 60, padding: 20 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: 'var(--ink-100)', border: '1px solid var(--border)', borderRadius: 4, width: '100%', maxWidth: 360, padding: 18 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <div className="lb-eyebrow">Calculadora</div>
          <button onClick={onClose} style={{ background: 'transparent', border: 0, color: 'var(--ink-600)', cursor: 'pointer', fontSize: 18 }}>✕</button>
        </div>
        <div style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 4, padding: '12px 14px', marginBottom: 14, minHeight: 64, textAlign: 'right' }}>
          <div style={{ color: 'var(--ink-600)', fontSize: 14, minHeight: 18, wordBreak: 'break-all' }}>{expr || '0'}</div>
          <div style={{ color: '#fff', fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 26 }}>{live !== '' ? money(Number(live)) : ''}</div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
          {keys.map(k => (
            <button key={k} onClick={() => press(k)} style={{
              gridColumn: k === '0' ? 'span 2' : 'span 1',
              padding: '16px 0', fontSize: 18, fontWeight: 700, cursor: 'pointer', borderRadius: 4,
              color: '#fff',
              background: k === '=' ? 'var(--red-600, #C8102E)' : (k === 'C' ? 'var(--ink-300, #2a2a2a)' : (isOp(k) || k === '⌫' ? 'var(--ink-300, #2a2a2a)' : 'var(--ink-200)')),
              border: '1px solid var(--border)',
            }}>{k}</button>
          ))}
        </div>
      </div>
    </div>
  );
}

function GuardarropaPad({ products, add }) {
  // En guardarropa: si hay productos (precios fijos) los muestra como botones; si no, se usa el campo precio del ticket.
  const list = products.filter(p => p.active !== false);
  if (!list.length) return (
    <div style={{ color: 'var(--ink-600)', fontSize: 13, padding: 30 }}>
      Caja de guardarropa. Cargá la descripción de la prenda y el precio en el panel de la derecha, luego cobrá. El ticket sale con número correlativo.
    </div>
  );
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: 10 }}>
      {list.map(p => (
        <button key={p.id} onClick={() => add(p)} style={{ background: 'var(--ink-200)', border: '1px solid var(--border)', borderRadius: 4, padding: '16px 12px', cursor: 'pointer', color: '#fff' }}>
          <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 8 }}>{p.name}</div>
          <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, color: 'var(--ink-800)' }}>{money(p.price)}</div>
        </button>
      ))}
    </div>
  );
}

// Los dos tickets del guardarropa, después de cobrar. Cada uno se imprime por
// separado y las veces que haga falta: el número de percha ya está reservado por la
// venta, así que quedarse sin papel no genera "perchas fantasma".
function PrintTicketsModal({ sale, onPrint, onClose }) {
  const [hecho, setHecho] = useState({ cliente: false, percha: false });
  const [busy, setBusy] = useState(null);

  async function imprimir(variant) {
    setBusy(variant);
    try {
      const ok = await onPrint(variant);
      // Solo se marca como impreso si de verdad salió.
      if (ok !== false) setHecho(h => ({ ...h, [variant]: true }));
    } finally { setBusy(null); }
  }

  // Sin la clase lb-btn a propósito: esa centra el contenido en UNA línea y el
  // subtítulo se desbordaba fuera del botón. Acá el botón es un bloque de dos
  // renglones que ajusta el texto.
  const Boton = ({ variant, titulo, sub }) => {
    const listo = hecho[variant];
    return (
      <button
        onClick={() => imprimir(variant)} disabled={busy === variant}
        style={{
          display: 'block', width: '100%', textAlign: 'left', cursor: 'pointer',
          padding: '14px 16px', borderRadius: 999,
          background: listo ? 'transparent' : '#fff',
          color: listo ? '#fff' : '#000',
          border: listo ? '1px solid rgba(255,255,255,0.35)' : '1px solid #fff',
          opacity: busy === variant ? 0.7 : 1,
        }}
      >
        <div style={{ fontSize: 14.5, fontWeight: 700, lineHeight: 1.25 }}>
          {listo ? '✓ ' : ''}{busy === variant ? 'Imprimiendo…' : titulo}
        </div>
        <div style={{ fontSize: 11.5, opacity: 0.7, marginTop: 3, lineHeight: 1.3, whiteSpace: 'normal' }}>
          {listo ? 'Ya se imprimió · tocá para imprimir otra vez' : sub}
        </div>
      </button>
    );
  };

  return (
    <div className="lb-modal-backdrop">
      <div className="lb-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 420, padding: 26, textAlign: 'center' }}>
        <div className="lb-eyebrow" style={{ color: '#2ecc71' }}>Cobrado</div>
        <div style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 64, lineHeight: 1, color: '#fff', margin: '6px 0 2px' }}>
          {sale.hanger_number}
        </div>
        <div style={{ fontSize: 13, color: 'var(--ink-700)' }}>
          {sale.note || 'Sin descripción'}{sale.hanger_extra ? ' · percha extra' : ''}
        </div>
        {sale.offline && <div style={{ fontSize: 11, color: '#d4af37', marginTop: 8, textTransform: 'uppercase', letterSpacing: '0.08em' }}>● Guardado sin conexión</div>}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 22 }}>
          <Boton variant="cliente" titulo="Imprimir ticket cliente" sub="Se lo lleva el cliente para retirar" />
          <Boton variant="percha" titulo="Imprimir ticket percha" sub="Queda con la prenda, con su descripción" />
        </div>

        <button className="lb-btn lb-btn--primary lb-btn--block" style={{ marginTop: 16, padding: 16 }} onClick={onClose}>
          {hecho.cliente && hecho.percha ? 'Listo · siguiente cliente' : 'Cerrar'}
        </button>
        {!(hecho.cliente && hecho.percha) && (
          <div style={{ fontSize: 11, color: 'var(--ink-600)', marginTop: 8 }}>
            Podés cerrar sin imprimir: la percha <b>{sale.hanger_number}</b> queda reservada y se reimprime tocándola en la grilla.
          </div>
        )}
      </div>
    </div>
  );
}

// Percha ocupada: devolver la prenda (libera el número) o reimprimir cualquiera de
// los dos tickets. La devolución pide confirmación porque liberar una percha que
// todavía tiene prenda hace que el próximo cliente reciba el mismo número.
function HangerBusyModal({ n, oc, onPrint, onReturn, onClose }) {
  const [confirmando, setConfirmando] = useState(false);
  const [busy, setBusy] = useState(null);

  async function imprimir(variant) {
    setBusy(variant);
    try { await onPrint(variant); } finally { setBusy(null); }
  }

  return (
    <div className="lb-modal-backdrop" onClick={onClose}>
      <div className="lb-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 400, padding: 26, textAlign: 'center' }}>
        <div className="lb-eyebrow">Percha ocupada</div>
        <div style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 60, lineHeight: 1, color: '#fff', margin: '6px 0 2px' }}>{n}</div>
        <div style={{ fontSize: 13.5, color: '#fff' }}>{oc?.prenda || 'Sin descripción'}</div>
        {oc?.at && <div style={{ fontSize: 11, color: 'var(--ink-600)', marginTop: 4 }}>Desde {new Date(oc.at).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })} hs</div>}

        {!confirmando ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 22 }}>
            <button className="lb-btn lb-btn--primary lb-btn--block" style={{ padding: 16 }} onClick={() => setConfirmando(true)}>
              Devolver prenda
            </button>
            <button className="lb-btn lb-btn--white lb-btn--block" style={{ whiteSpace: 'normal', lineHeight: 1.3 }} onClick={() => imprimir('cliente')} disabled={busy === 'cliente'}>
              {busy === 'cliente' ? 'Imprimiendo…' : 'Reimprimir ticket cliente'}
            </button>
            <button className="lb-btn lb-btn--white lb-btn--block" style={{ whiteSpace: 'normal', lineHeight: 1.3 }} onClick={() => imprimir('percha')} disabled={busy === 'percha'}>
              {busy === 'percha' ? 'Imprimiendo…' : 'Reimprimir ticket percha'}
            </button>
            <button className="lb-btn lb-btn--ghost lb-btn--block" onClick={onClose}>Cancelar</button>
          </div>
        ) : (
          // Confirmación que dice QUÉ se está liberando, no un "¿seguro?" genérico.
          <div style={{ marginTop: 20 }}>
            <div style={{ fontSize: 13.5, color: 'var(--ink-800)', lineHeight: 1.5, background: 'rgba(200,16,46,0.10)', border: '1px solid rgba(200,16,46,0.4)', borderRadius: 6, padding: '12px 14px' }}>
              ¿Devolver la percha <b style={{ color: '#fff' }}>{n}</b>{oc?.prenda ? <> — <b style={{ color: '#fff' }}>{oc.prenda}</b></> : ''}?<br />
              <span style={{ fontSize: 12, color: 'var(--ink-700)' }}>El número queda libre para otro cliente. La venta cobrada no se anula.</span>
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
              <button className="lb-btn lb-btn--ghost lb-btn--block" onClick={() => setConfirmando(false)}>No</button>
              <button className="lb-btn lb-btn--primary lb-btn--block" onClick={onReturn}>Sí, devolver</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return <div className="lb-field"><div className="lb-label">{label}</div>{children}</div>;
}
